import axios from 'axios'
import { getSocketId } from './echo'
import i18n from '../i18n'
import { getUserPlaceholderImage } from '../utils/imageUtils'
import { CLINIC_SETUP_DATA_ENDPOINT } from './clinicService'
import { withAuditLog, ACTION_TYPES } from './auditLogService'

// Create axios instance with default config for Laravel
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:8000',
  timeout: 600000, // 10 minutes (600000ms) - increased for large file uploads
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    'Pragma': 'no-cache',
    'Expires': '0',
  },
  withCredentials: false, // Token-based auth
})

// FIXED: Add variables to prevent infinite refresh loops
let isRefreshing = false
let failedQueue = []

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })

  failedQueue = []
}

// FIXED: Enhanced request interceptor
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    // Add Accept-Language header for backend localization
    config.headers['Accept-Language'] = i18n.language || 'en'

    return config;
  },
  (error) => {
    return Promise.reject(error)
  }
)

// FIXED: Enhanced response interceptor with proper refresh token handling
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config

    // Handle 401 Unauthorized with token refresh
    if (error.response?.status === 401) {
      const requestUrl = originalRequest?.url || ''
      
      // Explicit logging for WebView developers tracking 401 issues
      console.error('[WebView Auth Error] 401 Unauthorized on:', requestUrl, {
        authHeaderPresent: !!originalRequest?.headers?.Authorization,
        tokenExistsInStorage: !!localStorage.getItem('access_token')
      });

      // If the refresh token endpoint itself returns 401, logout immediately
      const isRefreshTokenRequest = requestUrl.includes('/api/v1/refresh-token') ||
        requestUrl.includes('/refresh-token')

      if (isRefreshTokenRequest) {
        // Refresh token is invalid, logout immediately
        processQueue(error, null)
        clearAuthDataAndRedirect()
        return Promise.reject(error)
      }

      // If request has already been retried, logout (prevents infinite loops)
      if (originalRequest._retry) {
        clearAuthDataAndRedirect()
        return Promise.reject(error)
      }

      // If we're already refreshing, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`
          return api(originalRequest)
        }).catch(err => {
          return Promise.reject(err)
        })
      }

      originalRequest._retry = true
      isRefreshing = true

      try {
        const refreshToken = localStorage.getItem('refresh_token')

        if (!refreshToken) {
          // No refresh token, clear auth and redirect
          clearAuthDataAndRedirect()
          return Promise.reject(error)
        }

        // FIXED: Use correct endpoint for token refresh
        const response = await api.post('/api/v1/refresh-token', {
          refresh_token: refreshToken
        })

        const { access_token, refresh_token: newRefreshToken } = response.data

        // Update tokens
        localStorage.setItem('access_token', access_token)
        if (newRefreshToken) {
          localStorage.setItem('refresh_token', newRefreshToken)
        }

        // Process the queue
        processQueue(null, access_token)

        // Retry the original request
        originalRequest.headers.Authorization = `Bearer ${access_token}`

        try {
          const retryResponse = await api(originalRequest)
          return retryResponse
        } catch (retryError) {
          // If retry after refresh also returns 401, logout
          if (retryError.response?.status === 401) {
            clearAuthDataAndRedirect()
          }
          return Promise.reject(retryError)
        }

      } catch (refreshError) {
        // Refresh failed (including 401), clear auth and redirect
        processQueue(refreshError, null)
        clearAuthDataAndRedirect()
        return Promise.reject(refreshError)
      } finally {
        isRefreshing = false
      }
    }

    return Promise.reject(error)
  }
)

// FIXED: Helper function to clear auth data and redirect (prevents loops)
const clearAuthDataAndRedirect = () => {
  const authKeys = ['access_token', 'refresh_token', 'user_data', 'device_id', 'verification_data', 'verification_id']
  authKeys.forEach(key => localStorage.removeItem(key))

  // Only redirect if not already on login page to prevent loops
  if (!window.location.pathname.includes('/auth/login') && !window.location.pathname.includes('/login')) {
    // Use replace to prevent back button issues
    window.location.replace('/auth/login')
  }
}

export default api

// FIXED: Auth API endpoints with CORRECT backend endpoints
export const authAPI = {
  // FIXED: Login with correct endpoint
  login: (credentials) => {
    return api.post('/api/v1/login', {
      identifier: credentials.identifier,
      identifier_type: credentials.identifier_type,
      password: credentials.password,
      remember_me: credentials.remember_me || false,
      device_info: credentials.device_info
    })
  },

  // FIXED: Register with correct endpoint
  register: (userData) => api.post('/api/v1/register', userData),

  // FIXED: Logout with correct endpoint
  logout: () => api.post('/api/v1/logout'),

  // FIXED: Refresh token with correct endpoint
  refreshToken: () => {
    const refreshToken = localStorage.getItem('refresh_token')
    if (!refreshToken) {
      throw new Error('No refresh token available')
    }
    return api.post('/api/v1/refresh-token', { refresh_token: refreshToken })
  },

  // FIXED: Get current user profile with CORRECT endpoint
  getProfile: (config = {}) => api.get('/api/v1/user-profile', config),

  // FIXED: Update profile with correct endpoint
  updateProfile: (userData) => {
    // Handle form data for file uploads
    if (userData.profile_image && userData.profile_image instanceof File) {
      const formData = new FormData()

      // Add text fields
      Object.keys(userData).forEach(key => {
        if (key !== 'profile_image' && userData[key] !== null && userData[key] !== undefined) {
          formData.append(key, userData[key])
        }
      })

      // Add profile image
      formData.append('profile_image', userData.profile_image)

      return api.post(`${import.meta.env.VITE_API_URL}/api/v1/user-profile`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 600000, // 10 minutes for file uploads
      })
    }

    return api.put(`${import.meta.env.VITE_API_URL}/api/v1/user-profile`, userData)
  },

  // Upload avatar
  uploadAvatar: (file) => {
    const formData = new FormData()
    formData.append('profile_image', file)
    return api.post(`${import.meta.env.VITE_API_URL}/api/v1/user-profile/upload-image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 600000, // 10 minutes for file uploads
    })
  },

  // Password reset
  requestPasswordReset: (email) => api.post(`${import.meta.env.VITE_API_URL}/api/v1/auth/password/reset`, { email }),
  resetPassword: (data) => api.post(`${import.meta.env.VITE_API_URL}/api/v1/auth/password/reset/confirm`, data),

  sendPasswordResetLink: (data) => api.post(`${import.meta.env.VITE_API_URL}/api/v1/auth/password/send-reset-link`, data),
  // Multi-step registration
  initiateRegistration: (userData) => api.post(`${import.meta.env.VITE_API_URL}/api/v1/auth/register/initiate`, userData),
  verifyOtp: (verificationData) => api.post(`${import.meta.env.VITE_API_URL}/api/v1/auth/register/verify-otp`, verificationData),
  resendOtp: (data) => api.post(`${import.meta.env.VITE_API_URL}/api/v1/auth/register/resend-otp`, data),
  createPassword: (data) => api.post(`${import.meta.env.VITE_API_URL}/api/v1/auth/register/create-password`, data),

  // Session management
  logoutAllSessions: () => api.post(`${import.meta.env.VITE_API_URL}/api/v1/user-sessions/logout-all`),
  logoutOtherSessions: () => api.post(`${import.meta.env.VITE_API_URL}/api/v1/user-sessions/logout-others`),
  getSessions: () => api.get(`${import.meta.env.VITE_API_URL}/api/v1/user-sessions`),
  revokeSession: (sessionId) => api.delete(`${import.meta.env.VITE_API_URL}/api/v1/user-sessions/${sessionId}`),

  // Apple Authentication
  appleRedirect: () => api.get(`${import.meta.env.VITE_API_URL}/api/v1/auth/apple/redirect`),
  appleCallback: (data) => api.post(`${import.meta.env.VITE_API_URL}/api/v1/auth/apple/callback`, data),
  verifyAppleLogin: (data) => api.post(`${import.meta.env.VITE_API_URL}/api/v1/auth/apple/verify-login`, data),
  verifyAppleRegistration: (data) => api.post(`${import.meta.env.VITE_API_URL}/api/v1/auth/apple/verify-registration`, data),

  // Clear auth data helper
  clearAuthData: clearAuthDataAndRedirect
}

// FIXED: Pet API endpoints with CORRECT backend endpoints
export const petAPI = {
  // Get user's pets
  getMyPets: (params = {}) => api.get(`${import.meta.env.VITE_API_URL}/api/v1/pets/my-pets`, { params }),
  getOtherPets: (profileId) => api.get(`${import.meta.env.VITE_API_URL}/api/v1/pets/user/${profileId}`),

  // Get all pets (with filters)
  getPets: (params = {}) => api.get(`${import.meta.env.VITE_API_URL}/api/v1/pets`, { params }),

  // Get single pet
  getPet: (petId) => {
    if (!petId || petId === ':petId') {
      return Promise.reject(new Error('Invalid pet ID'));
    }
    return api.get(`${import.meta.env.VITE_API_URL}/api/v1/pets/${petId}`);
  },
  getOtherPet: (petId) => api.get(`${import.meta.env.VITE_API_URL}/api/v1/pets/others/profile/${petId}`),

  // Create new pet
  createPet: (petData) => {
    // Handle form data for file uploads
    if (petData.profile_image || petData.gallery_images) {
      const formData = new FormData()

      // Add text fields
      Object.keys(petData).forEach(key => {
        if (key !== 'profile_image' && key !== 'gallery_images' && key !== 'remove_profile_image') {
          if (petData[key] !== null && petData[key] !== undefined) {
            formData.append(key, petData[key])
          }
        }
      })

      // Add profile image
      if (petData.profile_image) {
        formData.append('profile_image', petData.profile_image)
      }

      // Add gallery images
      if (petData.gallery_images) {
        Array.from(petData.gallery_images).forEach((file, index) => {
          formData.append(`gallery_images[${index}]`, file)
        })
      }

      return api.post(`${import.meta.env.VITE_API_URL}/api/v1/pets`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 600000, // 10 minutes for file uploads
      })
    }

    return api.post(`${import.meta.env.VITE_API_URL}/api/v1/pets`, petData)
  },

  // Update pet
  updatePet: (petId, petData) => {
    // Handle form data for file uploads
    if (petData.profile_image || petData.gallery_images || petData.remove_profile_image) {
      const formData = new FormData()

      // Laravel requires _method for PUT with FormData
      formData.append('_method', 'PUT')

      // Add text fields
      Object.keys(petData).forEach(key => {
        if (key !== 'profile_image' && key !== 'gallery_images' && key !== 'remove_profile_image') {
          if (petData[key] !== null && petData[key] !== undefined) {
            formData.append(key, petData[key])
          }
        }
      })

      // Add profile image
      if (petData.profile_image) {
        formData.append('profile_image', petData.profile_image)
      }

      // Add remove_profile_image flag
      if (petData.remove_profile_image) {
        formData.append('remove_profile_image', 'true')
      }

      // Add gallery images
      if (petData.gallery_images) {
        Array.from(petData.gallery_images).forEach((file, index) => {
          formData.append(`gallery_images[${index}]`, file)
        })
      }

      return api.post(`${import.meta.env.VITE_API_URL}/api/v1/pets/${petId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 600000, // 10 minutes for file uploads
      })
    }

    return api.put(`${import.meta.env.VITE_API_URL}/api/v1/pets/${petId}`, petData)
  },

  // Delete pet
  deletePet: (petId) => api.delete(`${import.meta.env.VITE_API_URL}/api/v1/pets/${petId}`),

  // Get public pets (no auth required)
  getPublicPets: (params = {}) => api.get(`${import.meta.env.VITE_API_URL}/api/v1/pets/public`, { params }),

  // Get pets by species
  getPetsBySpecies: (species, params = {}) =>
    api.get(`${import.meta.env.VITE_API_URL}/api/v1/species`, { params }),

  // Get Pets by Breed
  getBreedBySpecies: (species, params = {}) =>
    api.get(`${import.meta.env.VITE_API_URL}/api/v1/breed-types/species/${species}`, { params }),

  // Upload profile image
  uploadProfileImage: (petId, file) => {
    const formData = new FormData()
    formData.append('profile_image', file)
    return api.post(`${import.meta.env.VITE_API_URL}/api/v1/pets/${petId}/profile-image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  },

  // Bulk operations
  bulkDeletePets: (petIds) =>
    api.post(`${import.meta.env.VITE_API_URL}/api/v1/pets/bulk-delete`, {
      pet_ids: petIds,
      confirm_deletion: true
    }),

  // Get complete medical history for a pet
  getCompleteMedicalHistory: (petId, params = {}) =>
    api.get(`${import.meta.env.VITE_API_URL}/api/v1/pet-health/medical-history/${petId}`, { params }),

  getPetVisits: (petId) => api.get(`${import.meta.env.VITE_API_URL}/api/v1/pets/${petId}/visits`),

  // Vaccination records (pet-health API)
  getVaccinations: (petId, params = {}) =>
    api.get(`${import.meta.env.VITE_API_URL}/api/v1/pet-health/vaccinations`, {
      params: { pet_id: petId, per_page: 100, limit: 100, simple: true, ...params }
    }),

  createVaccination: (data) =>
    api.post(`${import.meta.env.VITE_API_URL}/api/v1/pet-health/vaccinations`, data),

  updateVaccination: (vaccinationId, data) =>
    api.put(`${import.meta.env.VITE_API_URL}/api/v1/pet-health/vaccinations/${vaccinationId}`, data),
}

/**
 * Sync visit services that have due dates into the pet's vaccination records.
 * This ensures the doctor-entered due date on a service becomes the vaccine's next_due_date.
 */
export const syncVisitServicesToVaccinations = async (petId, services = [], visitDate = null) => {
  if (!petId || !Array.isArray(services) || services.length === 0) return;

  // Only services with has_due_date=true and an actual due_date
  const vaccineServices = services.filter(
    (s) => s.has_due_date === true && s.due_date
  );
  if (vaccineServices.length === 0) return;

  const todayStr = visitDate || new Date().toISOString().split('T')[0];

  try {
    // Fetch existing vaccinations to avoid duplicates and fix backend-generated wrong records
    const existingRes = await petAPI.getVaccinations(petId);
    const existingVaccinations = existingRes.data?.data ?? existingRes.data ?? [];

    for (const svc of vaccineServices) {
      const vaccineName = svc.service_name_en || svc.service_name_ar || svc.name || 'Unknown Vaccine';
      const dueDate = svc.due_date;

      // Look for an existing record with the same name
      const matching = existingVaccinations.find((v) => {
        const name = v.vaccine_name || v['Vaccine Name'] || '';
        return name.toLowerCase().trim() === vaccineName.toLowerCase().trim();
      });

      if (matching) {
        const currentNextDue = matching.next_due_date || matching['Next Due Date'];
        // If the existing record already has the correct due date, skip
        if (currentNextDue === dueDate) continue;

        // Otherwise update it (likely a backend auto-synced record with wrong date)
        const recordId = matching.id ?? matching.ID ?? matching.Id ?? matching._id;
        if (!recordId) continue;

        const payload = {
          ...matching,
          next_due_date: dueDate,
          note: matching.note || `Synced from visit service`,
        };
        await petAPI.updateVaccination(recordId, payload);
      } else {
        // Create a new vaccination record
        await petAPI.createVaccination({
          pet_id: petId,
          vaccine_name: vaccineName,
          date_administered: todayStr,
          next_due_date: dueDate,
          note: `Synced from visit service`,
          veterinarian: null
        });
      }
    }
  } catch (error) {
    // Non-blocking: log but don't fail checkout
    console.warn('Failed to sync visit services to vaccinations:', error);
  }
};

// Joint Guardianship API
export const petGuardianshipAPI = {
  // Get all guardians for a specific pet
  getPetGuardians: (petId) => api.get(`${import.meta.env.VITE_API_URL}/api/v1/pets/${petId}/guardians`),

  // Send an invitation to a user to become a guardian
  inviteGuardian: (petId, userData) => api.post(`${import.meta.env.VITE_API_URL}/api/v1/pets/${petId}/guardians/invite`, userData),

  // Get all pending guardianship requests for the current user
  getMyGuardianshipRequests: () => api.get(`${import.meta.env.VITE_API_URL}/api/v1/pets/guardianship/requests`),

  // Respond (accept/reject) to a guardianship request
  respondToRequest: (requestId, action) => api.post(`${import.meta.env.VITE_API_URL}/api/v1/pets/guardianship/requests/${requestId}/respond`, { action }),

  // Remove a guardian (primary owner only)
  removeGuardian: (petId, userId) => api.delete(`${import.meta.env.VITE_API_URL}/api/v1/pets/${petId}/guardians/${userId}`),

  // Leave guardianship (for the guardian themselves)
  leaveGuardianship: (petId) => api.post(`${import.meta.env.VITE_API_URL}/api/v1/pets/${petId}/guardians/leave`),
}

// FIXED: Posts API endpoints with CORRECT backend endpoints
export const postsAPI = {
  // Get consolidated feed
  getConsolidatedFeed: (params = {}) => {
    // Ensure we have authentication before making the call
    const token = localStorage.getItem('access_token')
    if (!token) {
      return Promise.reject(new Error('Authentication required'))
    }

    // Default params
    const defaultParams = {
      postPage: 1,  // Default to page 1 for posts
      storyPage: 1, // Default to page 1 for stories
      per_page: 20,
      include_user_reactions: true,
      include_buddies: true,
      include_friends: true,
      ...params
    }

    // Map 'page' to 'postPage' for backward compatibility if provided
    if (params.page && !params.postPage) {
      defaultParams.postPage = params.page;
    }

    return api.get(`${import.meta.env.VITE_API_URL}/api/v1/posts/consolidated-feed`, { params: defaultParams })
  },
  // FIXED: Get all posts - use the protected route that exists
  getPosts: (params = {}) => {
    // Ensure we have authentication before making the call
    const token = localStorage.getItem('access_token')
    if (!token) {
      return Promise.reject(new Error('Authentication required'))
    }
    return api.get(`${import.meta.env.VITE_API_URL}/api/v1/posts`, { params })
  },

  // Get user's posts
  getMyPosts: (params = {}) => api.get(`${import.meta.env.VITE_API_URL}/api/v1/posts/my-posts`, { params }),
  getOtherPosts: (profileId, params = {}) => api.get(`${import.meta.env.VITE_API_URL}/api/v1/posts/user/${profileId}`, { params }),

  // Get total posts count including channel posts (best effort via pagination metadata)
  getTotalPostsCount: async (userId = null) => {
    try {
      let response;
      if (userId) {
        // For other users: use dedicated user-posts endpoint
        response = await postsAPI.getOtherPosts(userId, { per_page: 1 });
      } else {
        // For current user: use my-posts endpoint
        response = await postsAPI.getMyPosts({ per_page: 1 });
      }
      const data = response?.data;
      if (!data) return null;
      return data.meta?.total ?? data.pagination?.total ?? data.total ?? data.data?.total ?? null;
    } catch {
      // Silently fail — caller will fall back to profile count
      return null;
    }
  },

  // Get single post
  getPost: (postId) => api.get(`${import.meta.env.VITE_API_URL}/api/v1/posts/${postId}`),

  // Create new post
  createPost: (postData, onUploadProgress) => {
    // Validate postData
    if (!postData || typeof postData !== 'object') {
      return Promise.reject(new Error('Post data is required and must be an object'))
    }

    // Validate and normalize post_type - only allow valid values: post, question, poll, story
    const validPostTypes = ['post', 'question', 'poll', 'story'];
    const postType = postData.post_type || postData.type || 'post';
    const normalizedPostType = validPostTypes.includes(postType) ? postType : 'post';

    // Handle form data for file uploads
    if (postData.files && Array.isArray(postData.files) && postData.files.length > 0) {
      const formData = new FormData()
      formData.append('content', postData.content || '')

      // Ensure channel + type metadata are preserved when uploading files
      if (postData.channel_id) {
        formData.append('channel_id', postData.channel_id)
      }
      // Always set valid post_type
      formData.append('post_type', normalizedPostType)

      // Privacy setting
      if (postData.privacy) {
        formData.append('privacy', postData.privacy)
      }

      // Poll metadata (in case we ever support images on polls)
      if (Array.isArray(postData.poll_options)) {
        postData.poll_options.forEach(opt => {
          if (opt !== undefined && opt !== null && String(opt).trim() !== '') {
            formData.append('poll_options[]', opt)
          }
        })
      }
      if (postData.poll_duration !== undefined && postData.poll_duration !== null) {
        formData.append('poll_duration', postData.poll_duration)
      }

      // Append all files to the FormData
      postData.files.forEach((file, index) => {
        if (file instanceof File) {
          formData.append('files[]', file)
        }
      })

      // Append document files if present
      if (postData.documents && Array.isArray(postData.documents) && postData.documents.length > 0) {
        postData.documents.forEach((file, index) => {
          if (file instanceof File) {
            formData.append('documents[]', file)
          }
        })
      }

      const config = {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 600000, // 10 minutes for file uploads (600000ms)
      }

      // Add upload progress callback if provided
      if (onUploadProgress && typeof onUploadProgress === 'function') {
        config.onUploadProgress = (progressEvent) => {
          if (progressEvent.total) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)
            // Show actual upload progress (0-100%) based on bytes sent
            // Progress reaches 100% when upload completes (loaded === total)
            onUploadProgress(percentCompleted)
          } else if (progressEvent.loaded && progressEvent.lengthComputable === false) {
            // Handle cases where total is not available
            onUploadProgress(50) // Show indeterminate progress
          }
        }
      }

      // Return promise with error handling
      return api.post(`${import.meta.env.VITE_API_URL}/api/v1/posts`, formData, config)
        .catch((error) => {
          // Reset progress on error
          if (onUploadProgress && typeof onUploadProgress === 'function') {
            onUploadProgress(0)
          }
          throw error
        })
    } else if (postData.image && postData.image instanceof File) {
      // Backward compatibility for single file
      const formData = new FormData()
      formData.append('content', postData.content || '')
      formData.append('files[]', postData.image)

      if (postData.channel_id) {
        formData.append('channel_id', postData.channel_id)
      }
      // Always set valid post_type
      formData.append('post_type', normalizedPostType)

      // Privacy setting
      if (postData.privacy) {
        formData.append('privacy', postData.privacy)
      }

      const config = {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 600000, // 10 minutes for file uploads (600000ms)
      }

      // Add upload progress callback if provided
      if (onUploadProgress && typeof onUploadProgress === 'function') {
        config.onUploadProgress = (progressEvent) => {
          if (progressEvent.total) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)
            // Show actual upload progress (0-100%) based on bytes sent
            // Progress reaches 100% when upload completes (loaded === total)
            onUploadProgress(percentCompleted)
          } else if (progressEvent.loaded && progressEvent.lengthComputable === false) {
            // Handle cases where total is not available
            onUploadProgress(50) // Show indeterminate progress
          }
        }
      }

      // Return promise with error handling
      return api.post(`${import.meta.env.VITE_API_URL}/api/v1/posts`, formData, config)
        .catch((error) => {
          // Reset progress on error
          if (onUploadProgress && typeof onUploadProgress === 'function') {
            onUploadProgress(0)
          }
          throw error
        })
    }

    // For JSON requests, ensure post_type is valid
    const jsonPostData = {
      ...postData,
      post_type: normalizedPostType
    };
    // Remove legacy 'type' field if it exists to avoid conflicts
    delete jsonPostData.type;

    return api.post(`${import.meta.env.VITE_API_URL}/api/v1/posts`, jsonPostData)
  },

  voteOnPoll: (postId, optionId) =>
    api.post(`${import.meta.env.VITE_API_URL}/api/v1/posts/${postId}/poll/vote`, {
      option_id: optionId,
    }),

  // FIXED: Update post - use the correct backend endpoint with enhanced error handling
  updatePost: (postId, postData) => {

    // Validate postId
    if (!postId) {
      return Promise.reject(new Error('Post ID is required'))
    }

    // Validate postData
    if (!postData || typeof postData !== 'object') {
      return Promise.reject(new Error('Post data is required and must be an object'))
    }

    // Validate and normalize post_type if provided - only allow valid values: post, question, poll, story
    const validPostTypes = ['post', 'question', 'poll', 'story'];
    let normalizedPostType = null;
    if (postData.post_type || postData.type) {
      const postType = postData.post_type || postData.type;
      normalizedPostType = validPostTypes.includes(postType) ? postType : null;
    }

    // Handle form data for file uploads or media removals
    if ((postData.files && Array.isArray(postData.files) && postData.files.length > 0) ||
      (postData.removedMediaIds && Array.isArray(postData.removedMediaIds) && postData.removedMediaIds.length > 0) ||
      postData.removeImage) {
      const formData = new FormData()
      formData.append('content', postData.content || '')

      // Append all files to the FormData
      if (postData.files && Array.isArray(postData.files)) {
        postData.files.forEach((file, index) => {
          if (file instanceof File) {
            formData.append('files[]', file)
          }
        })
      }

      // Add removed media IDs if present
      if (postData.removedMediaIds && Array.isArray(postData.removedMediaIds)) {
        postData.removedMediaIds.forEach(id => {
          formData.append('delete_media_ids[]', id)
        })
      }

      // Add flag to remove all media if specified
      if (postData.removeImage) {
        formData.append('remove_image', 'true')
      }

      // Add post_type if provided and valid
      if (normalizedPostType) {
        formData.append('post_type', normalizedPostType)
      }

      // Add privacy if provided
      if (postData.privacy) {
        formData.append('privacy', postData.privacy)
      }

      return api.post(`${import.meta.env.VITE_API_URL}/api/v1/posts/${postId}/update`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
    } else if (postData.image && postData.image instanceof File) {
      // Backward compatibility for single file
      const formData = new FormData()
      formData.append('content', postData.content || '')
      formData.append('files[]', postData.image)

      return api.post(`${import.meta.env.VITE_API_URL}/api/v1/posts/${postId}/update`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
    }

    // Handle JSON data update (content only)
    const jsonPostData = { ...postData };

    // Privacy is already included in postData spread, but ensure it's included
    // Add post_type if provided and valid, remove legacy 'type' field
    if (normalizedPostType) {
      jsonPostData.post_type = normalizedPostType;
    }
    delete jsonPostData.type; // Remove legacy type field to avoid conflicts

    return api.post(`${import.meta.env.VITE_API_URL}/api/v1/posts/${postId}/update`, jsonPostData)
  },

  // Delete post
  deletePost: (postId) => api.delete(`${import.meta.env.VITE_API_URL}/api/v1/posts/${postId}`),

  // Rate a post - backend handles all calculations and returns updated rating data
  ratePost: (postId, rating) => {
    return api.post(`${import.meta.env.VITE_API_URL}/api/v1/posts/${postId}/rate`, {
      rating: rating // 0.5, 1.0, 1.5, 2.0, 2.5, 3.0, 3.5, 4.0, 4.5, 5.0
    });
  },

  // Get posts by status
  getPostsByStatus: (status, params = {}) =>
    api.get(`${import.meta.env.VITE_API_URL}/api/v1/posts/status/${status}`, { params }),

  // Search posts
  searchPosts: (params = {}) => api.get(`${import.meta.env.VITE_API_URL}/api/v1/posts/search`, { params }),
}

// FIXED: Comments API endpoints with CORRECT backend endpoints
export const commentsAPI = {
  // Get comments for a post
  getPostComments: (postId, params = {}) =>
    api.get(`${import.meta.env.VITE_API_URL}/api/v1/posts/${postId}/comments`, { params }),

  // Get single comment
  getComment: (postId, commentId) =>
    api.get(`${import.meta.env.VITE_API_URL}/api/v1/posts/${postId}/comments/${commentId}`),

  // Create comment
  createComment: (postId, commentData) =>
    api.post(`${import.meta.env.VITE_API_URL}/api/v1/posts/${postId}/comments`, {
      text: commentData.text || commentData.content
    }),

  // Update comment
  updateComment: (postId, commentId, commentData) =>
    api.put(`${import.meta.env.VITE_API_URL}/api/v1/posts/${postId}/comments/${commentId}`, {
      text: commentData.text || commentData
    }),

  // Delete comment
  deleteComment: (postId, commentId) =>
    api.delete(`${import.meta.env.VITE_API_URL}/api/v1/posts/${postId}/comments/${commentId}`),

  // Reply to a comment
  createReply: (postId, commentId, replyData) =>
    api.post(`${import.meta.env.VITE_API_URL}/api/v1/posts/${postId}/comments/${commentId}/reply`, {
      text: replyData.text || replyData
    }),

  // Get replies for a comment
  getCommentReplies: (postId, commentId, params = {}) =>
    api.get(`${import.meta.env.VITE_API_URL}/api/v1/posts/${postId}/comments/${commentId}/replies`, { params }),

  // Comment Reactions API endpoints
  // Add/update reaction to a comment
  addReaction: (postId, commentId, reactionData) =>
    api.post(`${import.meta.env.VITE_API_URL}/api/v1/posts/${postId}/comments/${commentId}/reactions`, reactionData),

  // Remove reaction from a comment
  removeReaction: (postId, commentId) =>
    api.delete(`${import.meta.env.VITE_API_URL}/api/v1/posts/${postId}/comments/${commentId}/reactions`),

  // Get all reactions for a comment
  getReactions: (postId, commentId, params = {}) =>
    api.get(`${import.meta.env.VITE_API_URL}/api/v1/posts/${postId}/comments/${commentId}/reactions`, { params }),

  // Get reactions by type for a comment
  getReactionsByType: (postId, commentId, type, params = {}) =>
    api.get(`${import.meta.env.VITE_API_URL}/api/v1/posts/${postId}/comments/${commentId}/reactions/type/${type}`, { params }),

  // Get user's reaction to a comment
  getUserReaction: (postId, commentId) =>
    api.get(`${import.meta.env.VITE_API_URL}/api/v1/posts/${postId}/comments/${commentId}/reactions/user`),
}

// FIXED: Likes API endpoints with CORRECT backend endpoints
export const likesAPI = {
  // Toggle like on a post (like/unlike)
  toggleLike: (postId) => {
    const payload = { post_id: postId.toString() }
    return api.post(`${import.meta.env.VITE_API_URL}/api/v1/posts/likes`, payload)
  },

  // Add reaction to a post
  addReaction: (postId, reactionType) => {
    const payload = {
      reactionable_type: "App\\Models\\Post",
      reactionable_id: postId,
      type: reactionType
    }
    return api.post(`${import.meta.env.VITE_API_URL}/api/v1/reactions`, payload)
  },

  // Remove reaction from a post
  removeReaction: (postId) => {
    const payload = {
      reactionable_type: "App\\Models\\Post",
      reactionable_id: postId
    }
    return api.post(`${import.meta.env.VITE_API_URL}/api/v1/reactions`, payload)
  },

  // Get reactions for a post
  getPostReactions: (postId, params = {}) => {
    return api.get(`${import.meta.env.VITE_API_URL}/api/v1/reactions`, {
      params: {
        ...params,
        reactionable_type: "App\\Models\\Post",
        reactionable_id: postId
      }
    })
  },

  // Add reaction to a comment
  addCommentReaction: (commentId, reactionType) => {
    const payload = {
      reactionable_type: "App\\Models\\PostComment",
      reactionable_id: commentId,
      type: reactionType
    }
    return api.post(`${import.meta.env.VITE_API_URL}/api/v1/reactions`, payload)
  },

  // Remove reaction from a comment
  removeCommentReaction: (commentId) => {
    const payload = {
      reactionable_type: "App\\Models\\PostComment",
      reactionable_id: commentId
    }
    return api.post(`${import.meta.env.VITE_API_URL}/api/v1/reactions`, payload)
  },

  // Get reactions for a comment
  getCommentReactions: (commentId, params = {}) => {
    return api.get(`${import.meta.env.VITE_API_URL}/api/v1/reactions`, {
      params: {
        ...params,
        reactionable_type: "App\\Models\\PostComment",
        reactionable_id: commentId
      }
    })
  },

  // Get all likes (admin functionality)
  getAllLikes: (params = {}) => api.get('/posts/likes', { params }),

  // Get specific like
  getLike: (likeId) => api.get(`/posts/likes/${likeId}`),

  // Delete like
  deleteLike: (likeId) => api.delete(`/posts/likes/${likeId}`),

  // Hide/Unhide post
  hidePost: (postId) => api.post(`${import.meta.env.VITE_API_URL}/api/v1/posts/${postId}/hide`),
  unhidePost: (postId) => api.delete(`${import.meta.env.VITE_API_URL}/api/v1/posts/${postId}/hide`),
  getHiddenPosts: (params = {}) => api.get(`${import.meta.env.VITE_API_URL}/api/v1/posts/hidden`, { params }),
}

// FIXED: Shares API endpoints with CORRECT backend endpoints
export const sharesAPI = {
  // Share a post
  sharePost: (postId) =>
    api.post(`${import.meta.env.VITE_API_URL}/api/v1/posts/${postId}/share`),

  // Get shared posts
  getSharedPosts: (postId, params = {}) =>
    api.get(`${import.meta.env.VITE_API_URL}/api/v1/posts/${postId}/shared`, { params }),

  // Get users who shared a post
  getPostSharers: (postId, params = {}) =>
    api.get(`${import.meta.env.VITE_API_URL}/api/v1/posts/${postId}/sharers`, { params }),
}

// Repost API endpoints
export const repostsAPI = {
  // Repost a post (with optional response text)
  repostPost: (postId, data = {}) =>
    api.post(`${import.meta.env.VITE_API_URL}/api/v1/posts/${postId}/repost`, data),

  // Get reposts of a post
  getReposts: (postId, params = {}) =>
    api.get(`${import.meta.env.VITE_API_URL}/api/v1/posts/${postId}/reposts`, { params }),

  // Delete a repost
  deleteRepost: (repostId) =>
    api.delete(`${import.meta.env.VITE_API_URL}/api/v1/posts/reposts/${repostId}`),
}



// FIXED: User API endpoints with CORRECT backend endpoints
export const userAPI = {
  // Profile management
  getProfile: () => api.get(`${import.meta.env.VITE_API_URL}/api/v1/user-profile`),
  updateProfile: (data) => api.put(`${import.meta.env.VITE_API_URL}/api/v1/user-profile`, data),

  // Block management
  blockUser: (userId) => api.post(`${import.meta.env.VITE_API_URL}/api/v1/users/${userId}/block`),
  unblockUser: (userId) => api.delete(`${import.meta.env.VITE_API_URL}/api/v1/users/${userId}/unblock`),
  getBlockedUsers: (params = {}) => api.get(`${import.meta.env.VITE_API_URL}/api/v1/users/blocked`, { params }),

  // Mute management
  muteUser: (userId) => api.post(`${import.meta.env.VITE_API_URL}/api/v1/users/${userId}/mute`),
  unmuteUser: (userId) => api.delete(`${import.meta.env.VITE_API_URL}/api/v1/users/${userId}/mute`),
  getMutedUsers: (params = {}) => api.get(`${import.meta.env.VITE_API_URL}/api/v1/users/muted`, { params }),

  // Avatar upload
  uploadAvatar: (file) => {
    const formData = new FormData()
    formData.append('profile_image', file)
    return api.post(`${import.meta.env.VITE_API_URL}/api/v1/user-profile/upload-image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  },

  // Preferences
  getPreferences: () => api.get(`${import.meta.env.VITE_API_URL}/api/v1/user-profile/preferences`),
  updatePreferences: (preferences) => api.put(`${import.meta.env.VITE_API_URL}/api/v1/user-profile/preferences`, preferences),

  // Notification settings
  getNotificationSettings: () => api.get(`${import.meta.env.VITE_API_URL}/api/v1/user-profile/notifications`),
  updateNotificationSettings: (settings) => api.put(`${import.meta.env.VITE_API_URL}/api/v1/user-profile/notifications`, settings),

  // Privacy settings
  getPrivacySettings: () => api.get(`${import.meta.env.VITE_API_URL}/api/v1/user-profile/privacy`),
  updatePrivacySettings: (settings) => api.put(`${import.meta.env.VITE_API_URL}/api/v1/user-profile/privacy`, settings),
  searchUsers: async (params = {}) => {
    try {
      const response = await api.get(`${import.meta.env.VITE_API_URL}/api/v1/users/search`, { params })

      // Check if response is array (old format)
      if (Array.isArray(response.data)) {
        return {
          ...response,
          data: {
            status: 'success',
            message: 'Users search completed successfully',
            data: response.data,
            pagination: {
              current_page: 1,
              per_page: response.data.length,
              total: response.data.length,
              last_page: 1,
              from: 1,
              to: response.data.length,
              has_more_pages: false
            }
          }
        }
      }

      // Return new format as is
      return response
    } catch (error) {
      throw error
    }
  },

  // Get other user profile by ID
  getUserProfile: (userId) => api.get(`${import.meta.env.VITE_API_URL}/api/v1/user-profile/${userId}?other_profile=true`),
}

// FIXED: Enhanced utility functions
export const authUtils = {
  // Check if user is authenticated
  isAuthenticated: () => {
    const token = localStorage.getItem('access_token')
    const userData = localStorage.getItem('user_data')

    if (!token || !userData || userData === 'undefined' || userData === 'null') {
      return false
    }

    try {
      const parsed = JSON.parse(userData)
      return !!(parsed && typeof parsed === 'object' && (parsed.id || parsed.user_id))
    } catch (error) {
      console.error('Error parsing user data:', error)
      return false
    }
  },

  // Get current user data
  getCurrentUser: () => {
    try {
      const userData = localStorage.getItem('user_data')
      if (!userData || userData === 'undefined' || userData === 'null') {
        return null
      }
      const parsed = JSON.parse(userData)
      // Validate that parsed data has required fields
      return (parsed && typeof parsed === 'object' && (parsed.id || parsed.user_id)) ? parsed : null
    } catch (error) {
      console.error('Error parsing user data:', error)
      return null
    }

  },

  // Set user data
  setUserData: (userData, token) => {
    if (userData && typeof userData === 'object') {
      localStorage.setItem('user_data', JSON.stringify(userData))
    }
    if (token) {
      localStorage.setItem('access_token', token)
    }
  },

  // Clear user data
  clearUserData: () => {
    const authKeys = ['access_token', 'refresh_token', 'user_data', 'device_id', 'verification_data', 'verification_id']
    authKeys.forEach(key => localStorage.removeItem(key))
  },

  // Get user type
  getUserType: () => {
    const user = authUtils.getCurrentUser()
    return user?.user_type || 'PET_GUARDIAN'
  }
}

// FIXED: Enhanced social media utility functions
export const socialUtils = {
  // Format timestamp for social media display
  formatTimestamp: (timestamp) => {
    try {
      const date = new Date(timestamp)
      const now = new Date()
      const diffInMinutes = Math.floor((now - date) / (1000 * 60))

      if (diffInMinutes < 1) return i18n.t('timeAgo.justNow')
      if (diffInMinutes < 60) {
        return i18n.t('timeAgo.minutes', { count: diffInMinutes })
      }

      const diffInHours = Math.floor(diffInMinutes / 60)
      if (diffInHours < 24) {
        return i18n.t('timeAgo.hours', { count: diffInHours })
      }

      const diffInDays = Math.floor(diffInHours / 24)
      if (diffInDays < 7) {
        return i18n.t('timeAgo.days', { count: diffInDays })
      }

      const diffInWeeks = Math.floor(diffInDays / 7)
      if (diffInWeeks < 4) {
        return i18n.t('timeAgo.weeks', { count: diffInWeeks })
      }

      return date.toLocaleDateString(i18n.language || undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    } catch (error) {
      console.error('Error formatting timestamp:', error)
      return i18n.t('timeAgo.unknown')
    }
  },

  // Helper to get localized name from object or string
  getLocalizedName: (value) => {
    if (!value) return null
    if (typeof value === "string") return value
    if (typeof value === "object") {
      const lang = i18n.language || "en"
      const nameEn = value.name_en || value.nameEn || value.en_name || value.label_en || value.title_en || value.en
      const nameAr = value.name_ar || value.nameAr || value.ar_name || value.label_ar || value.title_ar || value.ar

      if (lang === "ar") {
        return nameAr || nameEn || value.name || value.label || value.title || value.code || null
      }
      return nameEn || nameAr || value.name || value.label || value.title || value.code || null
    }
    return String(value)
  },

  // Get user type color for any user
  getUserTypeColor: (userType) => {
    const userTypeConfig = {
      'PET_GUARDIAN': '#8548A8', // Purple
      'VETERINARIAN': '#00D4B3', // Cyan
      'SERVICE_PROVIDER': '#D65745', // Orange
      'CLINIC_MODERATOR': '#2A8AD2', // Blue
      'ADMIN': '#667eea', // Purple/Blue gradient
    };
    return userTypeConfig[userType] || '#8548A8'; // Default to purple
  },

  // Truncate long URLs for display while keeping full URL for href
  truncateUrl: (url, maxLength = 40) => {
    if (!url || url.length <= maxLength) return url;
    try {
      const urlObj = new URL(url);
      const hostname = urlObj.hostname;
      const pathname = urlObj.pathname;
      let display = hostname + pathname;
      if (display.length > maxLength) {
        display = display.substring(0, maxLength - 3) + '...';
      }
      return display;
    } catch (e) {
      return url.substring(0, maxLength - 3) + '...';
    }
  },

  // FIXED: Transform backend post data to frontend format with error handling
  transformPostData: (post) => {
    if (!post || typeof post !== 'object') {
      console.warn('Invalid post data received:', post)
      return null
    }
    try {
      // Handle media array from backend
      let mediaArray = []
      if (post.media && Array.isArray(post.media)) {
        mediaArray = post.media.map(item => ({
          id: item.id || item.media_id || null, // Preserve media ID
          url: item.url || item,
          original_url: item.original_url || null,
          file_type: item.file_type || (item.url && (item.url.includes('.mp4') || item.url.includes('.mov') || item.url.includes('.webm')) ? 'video' : 'image'),
          thumbnail: item.thumbnail || null,
          resolutions: item.resolutions || null
        }))
      } else if (post.images && Array.isArray(post.images)) {
        mediaArray = post.images.map(item => ({
          id: item.id || item.media_id || null, // Preserve media ID
          url: item.url || item,
          file_type: 'image'
        }))
      } else if (post.image) {
        mediaArray = [{
          id: null, // Single image might not have ID
          url: post.image,
          file_type: post.image.includes('.mp4') || post.image.includes('.mov') || post.image.includes('.webm') ? 'video' : 'image'
        }]
      }

      // Get post_type from backend - preserve original value
      const backendTypeRaw =
        post.post_type ||
        post.postType ||
        post.type ||
        post.metadata?.type ||
        post.attributes?.type ||
        "";
      const originalPostType = backendTypeRaw.toString().trim().toLowerCase();

      // Normalize for type resolution (question, poll, story, repost get special treatment)
      let normalizedBackendType = originalPostType;
      if (!["question", "poll", "story", "post", "repost"].includes(normalizedBackendType)) {
        normalizedBackendType = "";
      }

      const pollOptions = Array.isArray(post.poll_options)
        ? post.poll_options.map(option => ({
          id: option.id || option.option_id || null,
          text: option.option || option.text || option.label || option,
          votes: option.votes || option.votes_count || 0,
        }))
        : [];

      let resolvedType = normalizedBackendType;
      // Preserve repost type if detected (check this first)
      if (post.type === 'repost' || post.is_repost || post.repost_id) {
        resolvedType = "repost";
      } else if (!resolvedType && post.is_question) {
        resolvedType = "question";
      } else if (!resolvedType && pollOptions.length > 0) {
        resolvedType = "poll";
      } else if (!resolvedType && (post.is_story || post.story_id)) {
        resolvedType = "story";
      }
      if (!resolvedType) {
        resolvedType = "";
      }

      // Channel / group context (for channel posts showing in main social feed)
      const channelSource =
        post.channel ||
        post.channel_data ||
        post.channel_info ||
        null;

      let channelContext = null;
      if (channelSource || post.channel_id) {
        const channelId =
          channelSource?.id ||
          channelSource?.channel_id ||
          post.channel_id ||
          null;

        const channelName =
          channelSource?.name ||
          post.channel_name ||
          null;

        const channelAvatar =
          channelSource?.profile_image_url ||
          channelSource?.profile_image ||
          channelSource?.avatar ||
          channelSource?.image ||
          null;

        channelContext = {
          id: channelId,
          name: channelName,
          profileImage: channelAvatar,
        };
      }

      // Transform repost data if present
      let repostedPostData = null;
      if (post.reposted_post || post.original_post) {
        const originalPost = post.reposted_post || post.original_post;

        // Transform author data - handle both author object and user object formats
        let transformedAuthor = null;
        if (originalPost.author && typeof originalPost.author === 'object') {
          // If author object exists, use it but ensure all fields are populated
          transformedAuthor = {
            id: originalPost.author.id || originalPost.user?.id || 0,
            name: originalPost.author.name ||
              (originalPost.author.first_name && originalPost.author.last_name
                ? `${originalPost.author.first_name} ${originalPost.author.last_name}`
                : originalPost.author.username ||
                (originalPost.user?.first_name && originalPost.user?.last_name
                  ? `${originalPost.user.first_name} ${originalPost.user.last_name}`
                  : originalPost.user?.username || 'Unknown')),
            username: originalPost.author.username || originalPost.user?.username,
            avatar: originalPost.author.avatar || originalPost.author.image || originalPost.user?.image || getUserPlaceholderImage(originalPost.user || originalPost.author),
            userType: originalPost.author.userType || originalPost.author.user_type || originalPost.user?.user_type || 'PET_GUARDIAN',
          };
        } else if (originalPost.user && typeof originalPost.user === 'object') {
          // If only user object exists, transform it to author format
          transformedAuthor = {
            id: originalPost.user.id || 0,
            name: originalPost.user.first_name && originalPost.user.last_name
              ? `${originalPost.user.first_name} ${originalPost.user.last_name}`
              : originalPost.user.username || 'Unknown',
            username: originalPost.user.username,
            avatar: originalPost.user.image || originalPost.user.profile_image_url || originalPost.user.profile_image || getUserPlaceholderImage(originalPost.user),
            userType: originalPost.user.user_type || 'PET_GUARDIAN',
          };
        } else {
          // Fallback if neither exists
          transformedAuthor = {
            id: 0,
            name: 'Unknown',
            username: 'unknown',
            avatar: getUserPlaceholderImage(null),
            userType: 'PET_GUARDIAN',
          };
        }

        // Normalize the reposted post's media array the same way the main post is normalized
        let repostedMediaArray = [];
        if (originalPost.media && Array.isArray(originalPost.media)) {
          repostedMediaArray = originalPost.media.map(item => ({
            id: item.id || item.media_id || null,
            url: item.url || item.path || item.src || null,
            original_url: item.original_url || null,
            file_type: item.file_type || (item.url && (item.url.includes('.mp4') || item.url.includes('.mov') || item.url.includes('.webm')) ? 'video' : 'image'),
            thumbnail: item.thumbnail || null,
            resolutions: item.resolutions || null,
          })).filter(item => item.url); // drop items with no URL
        } else if (originalPost.images && Array.isArray(originalPost.images)) {
          repostedMediaArray = originalPost.images.map(item => ({
            id: item.id || item.media_id || null,
            url: item.url || item.path || item.src || (typeof item === 'string' ? item : null),
            file_type: 'image',
          })).filter(item => item.url);
        } else if (originalPost.image) {
          repostedMediaArray = [{
            id: null,
            url: originalPost.image,
            file_type: originalPost.image.includes('.mp4') || originalPost.image.includes('.mov') || originalPost.image.includes('.webm') ? 'video' : 'image',
          }];
        }

        repostedPostData = {
          id: originalPost.id,
          author: transformedAuthor,
          content: originalPost.content || '',
          image: repostedMediaArray.length > 0 ? repostedMediaArray[0].url : (originalPost.image || null),
          media: repostedMediaArray,
          timestamp: originalPost.timestamp || socialUtils.formatTimestamp(originalPost.created_at),
          created_at: originalPost.created_at,
          // Include counts for display - use nullish coalescing to preserve 0 values
          reactions_count: originalPost.reactions_count ?? originalPost.reactions?.likes ?? 0,
          comments_count: originalPost.comments_count ?? originalPost.comments ?? originalPost.reactions?.comments ?? 0,
          reposts_count: originalPost.reposts_count ?? 0,
          shares_count: originalPost.shares_count ?? originalPost.shares ?? originalPost.reactions?.shares ?? 0,
          reactions: originalPost.reactions ?? {
            likes: originalPost.reactions_count ?? originalPost.reactions?.likes ?? 0,
            comments: originalPost.comments_count ?? originalPost.comments ?? originalPost.reactions?.comments ?? 0,
            shares: originalPost.shares_count ?? originalPost.shares ?? originalPost.reactions?.shares ?? 0,
          },
          // Include adoption post fields for adoption reposts
          adoption_post_id: originalPost.adoption_post_id ||
                           originalPost.metadata?.adoption_post_id ||
                           originalPost.metadata?.adoptionPostId ||
                           originalPost.metadata?.adoption_post?.id || 
                           originalPost.adoption_post?.id || null,
          adoption_post: originalPost.adoption_post ||
                        originalPost.metadata?.adoption_post || 
                        originalPost.metadata?.adoptionPost || null,
        };
      }

      return {
        id: post.id || 0,
        author: {
          id: post.user?.id || post.author?.id || 0,
          username:
            [post.user?.first_name, post.user?.last_name].filter(Boolean).join(' ') ||
            post.user?.username ||
            post.author?.username ||
            post.author?.name,
          name: post.author?.name ||
            [post.user?.first_name, post.user?.last_name].filter(Boolean).join(' ') ||
            post.user?.username,
          avatar: post.user?.image || post.author?.avatar || getUserPlaceholderImage(post.user),
          profileImage: post.user?.image || post.author?.avatar || getUserPlaceholderImage(post.user),
          userType: post.user?.user_type || post.author?.userType || 'PET_GUARDIAN',
          isSponsored: post.is_sponsored || false,
        },
        content: post.content || '',
        image: mediaArray.length > 0 ? mediaArray[0].url : null, // First media for backward compatibility
        media: mediaArray, // Array of all media
        timestamp: socialUtils.formatTimestamp(post.created_at),
        // Reaction counts - direct properties for easy access
        reactions_count: post.reactions_count ?? post.reactions?.likes ?? 0,
        comments_count: post.comments_count ?? post.comments ?? post.reactions?.comments ?? 0,
        shares_count: post.shares_count ?? post.shares ?? post.reactions?.shares ?? 0,
        reposts_count: post.reposts_count ?? 0,
        reactions: {
          likes: post.reactions_count ?? post.reactions?.likes ?? 0,
          comments: post.comments_count ?? post.comments ?? post.reactions?.comments ?? 0,
          shares: post.shares_count ?? post.shares ?? post.reactions?.shares ?? 0,
        },
        reaction_counts: post.reaction_counts || {},
        user_reaction_type: post.user_reaction_type || null,
        user_reaction_count: post.user_reaction_count || 0,
        user_reaction_emoji: post.user_reaction_emoji || null,
        user_reaction_id: post.user_reaction_id || null,
        isLiked: post.is_liked || false,
        has_reported: post.has_reported || false,
        hasMoreContent: post.content && post.content.length > 200,
        status: post.status || 'published',
        created_at: post.created_at,
        updated_at: post.updated_at,
        type: (post.type === 'repost' || post.is_repost || post.repost_id) ? 'repost' : resolvedType, // Preserve 'repost' type
        // Preserve post_type from backend for proper styling - use original value first
        post_type: originalPostType || post.post_type || normalizedBackendType || resolvedType || 'post',
        postType: originalPostType || post.post_type || normalizedBackendType || resolvedType || 'post', // Legacy compatibility
        // Preserve helper flags from backend
        is_question: post.is_question || resolvedType === 'question',
        is_story: post.is_story || resolvedType === 'story',
        is_poll: post.is_poll || resolvedType === 'poll',
        story_id: post.story_id || null,
        pollOptions: pollOptions.length > 0 ? pollOptions : undefined,
        poll_total_votes: post.poll_total_votes,
        poll_user_vote_option_id: post.poll_user_vote_option_id ?? post.poll_user_vote_id ?? null,
        poll_user_vote_id: post.poll_user_vote_id ?? post.poll_user_vote_option_id ?? null,
        poll_expires_at: post.poll_expires_at || null,
        poll_ended: post.poll_ended || false,
        channel: channelContext,
        // Repost fields - preserve all repost-related data
        is_repost: post.is_repost || false,
        repost_id: post.repost_id || null,
        repost_response_text: post.repost_response_text || post.response_text || null,
        user_has_reposted: post.user_has_reposted || false,
        reposted_post: repostedPostData,
        original_post: repostedPostData, // Same as reposted_post for compatibility
        // Adoption post fields - preserve for shared adoption posts
        adoption_post_id: post.adoption_post_id || 
                         post.metadata?.adoption_post_id || 
                         post.metadata?.adoptionPostId || 
                         post.metadata?.adoption_post?.id || 
                         post.adoption_post?.id ||
                         (post.post_type === 'adoption' ? post.id : null),
        adoption_post: post.adoption_post || 
                      post.metadata?.adoption_post || 
                      post.metadata?.adoptionPost || null,
        // Share URL from backend (included in consolidated-feed response)
        share_url: post.share_url || post.url || null,
        // Rating fields (from consolidated-feed or ratePost response) - backwards compatible
        average_rating: post.average_rating ?? post.avg_rating ?? null,
        user_rating: post.user_rating ?? post.user_rating_value ?? null,
        ratings_count: post.ratings_count ?? post.total_ratings ?? 0,
      }
    } catch (error) {
      console.error('Error transforming post data:', error, post)
      return null
    }
  },

  // FIXED: Transform backend comment data to frontend format with error handling
  transformCommentData: (comment) => {
    if (!comment || typeof comment !== 'object') {
      console.warn('Invalid comment data received:', comment)
      return null
    }

    try {
      return {
        id: comment.id || 0,
        text: comment.text || '',
        user: {
          id: comment.user?.id || 0,
          name: `${comment.user?.first_name || ''} ${comment.user?.last_name || ''}`.trim() || comment.user?.username,
          avatar: comment.user?.profile_image_url || getUserPlaceholderImage(comment.user),
          userType: comment.user?.user_type || 'PET_GUARDIAN',
        },
        reaction_counts: comment.reaction_counts || {},
        total_reactions: comment.total_reactions || 0,
        has_user_reaction: comment.has_user_reaction || false,
        user_reaction_type: comment.user_reaction_type || null,
        user_reaction_count: comment.user_reaction_count || 0,
        user_reaction_emoji: comment.user_reaction_emoji || null,
        user_reaction_id: comment.user_reaction_id || null,
        isLiked: comment.is_liked || false,
        timestamp: socialUtils.formatTimestamp(comment.created_at),
        created_at: comment.created_at,
        updated_at: comment.updated_at,
        replies_count: comment.replies_count || 0,
        parent_id: comment.parent_id || null,
        is_reply: comment.parent_id ? true : false,
      }
    } catch (error) {
      console.error('Error transforming comment data:', error, comment)
      return null
    }
  },

  // FIXED: Transform backend story data to frontend format with error handling
  transformStoryData: (story, formatTimestamp) => {
    if (!story || typeof story !== 'object') {
      console.warn('Invalid story data received:', story)
      return null
    }

    try {
      const userInfo = {
        id: story.user?.id || story.user?.name,
        name: `${story.user.first_name || ''} ${story.user.last_name || ''}`.trim() || story.user.username,
        avatar: story.user?.profile_image_url || getUserPlaceholderImage(story.user),
        isAddStory: false,
      }

      const baseStoryData = {
        user: userInfo,
        timestamp: story.created_at
          ? (formatTimestamp ? formatTimestamp(story.created_at) : socialUtils.formatTimestamp(story.created_at))
          : i18n.t('timeAgo.justNow'),
        content: story.caption || "",
        expiresAt: story.expires_at,
        isExpired: story.is_expired,
        isActive: story.is_active,
        timeRemaining: story.time_remaining,
        storyId: story.id,
        reactions_count: story.reactions_count || 0,
        views_count: story.views_count || story.viewers_count || 0,
        has_reacted: story.has_reacted || false,
        has_viewed: story.has_viewed || story.is_viewed || false,
      }

      // If story has media array with multiple items, return array of individual stories
      if (story.media && Array.isArray(story.media) && story.media.length > 0) {
        return story.media.map((mediaItem, mediaIndex) => ({
          id: `${story.id}-${mediaItem.id || mediaIndex}`,
          ...baseStoryData,
          image: mediaItem.file_type === 'video' ? null : mediaItem.url,
          video: mediaItem.file_type === 'video' ? mediaItem.url : null,
          type: mediaItem.file_type || (mediaItem.url && (mediaItem.url.includes('.mp4') || mediaItem.url.includes('.webm') || mediaItem.url.includes('.mov')) ? 'video' : 'image'),
        }))
      } else {
        // Fallback to media_url or single media item
        const mediaUrl = story.media_url || (story.media && story.media[0]?.url) || null
        const isVideo = mediaUrl && (mediaUrl.includes('.mp4') || mediaUrl.includes('.webm') || mediaUrl.includes('.mov'))

        return {
          id: story.id || 0,
          ...baseStoryData,
          image: isVideo ? null : mediaUrl,
          video: isVideo ? mediaUrl : null,
          type: isVideo ? 'video' : 'image',
        }
      }
    } catch (error) {
      console.error('Error transforming story data:', error, story)
      return null
    }
  },

  // Validate file for upload
  validateFile: (file, maxSizeInMB = 10, allowedTypes = ['image', 'video']) => {
    const errors = []

    if (!file) {
      errors.push('No file provided')
      return { isValid: false, errors }
    }

    // Check file size
    const maxSizeInBytes = maxSizeInMB * 1024 * 1024
    if (file.size > maxSizeInBytes) {
      errors.push(`File size must be less than ${maxSizeInMB}MB`)
    }

    // Check file type
    const fileType = file.type.split('/')[0]
    if (!allowedTypes.includes(fileType)) {
      errors.push(`File type must be: ${allowedTypes.join(', ')}`)
    }

    return {
      isValid: errors.length === 0,
      errors
    }
  },

  // Generate preview URL for file
  generateFilePreview: (file) => {
    return new Promise((resolve, reject) => {
      if (!file) {
        reject(new Error('No file provided'))
        return
      }

      const reader = new FileReader()
      reader.onload = (e) => resolve(e.target.result)
      reader.onerror = (e) => reject(new Error('Failed to read file'))
      reader.readAsDataURL(file)
    })
  }
}

// FIXED: Social API endpoints with CORRECT backend endpoints
export const socialAPI = {
  // Get suggested connections
  getSuggestedConnections: (params = {}) => api.get(`${import.meta.env.VITE_API_URL}/api/v1/social/suggested-connections`, { params }),

  // Get user connections/followers
  getConnections: (params = {}) => api.get(`${import.meta.env.VITE_API_URL}/api/v1/social/connections`, { params }),

  // Get mutual connects (friends) list for a user
  getConnects: (params = {}) =>
    api.get(`${import.meta.env.VITE_API_URL}/api/v1/social/connects`, { params }),

  // Send connection request
  sendConnectionRequest: (userId) => api.post(`${import.meta.env.VITE_API_URL}/api/v1/users/${userId}/follow`, { action: 'follow' }),
  // Accept connection request
  acceptConnectionRequest: (userId) => api.put(`${import.meta.env.VITE_API_URL}/api/v1/users/${userId}/approve-follow`),

  // Reject connection request
  rejectConnectionRequest: (userId) => api.delete(`${import.meta.env.VITE_API_URL}/api/v1/users/${userId}/reject-follow`),

  // Search users
  searchUsers: (params = {}) => api.get(`${import.meta.env.VITE_API_URL}/api/v1/users/search`, { params }),

  // Get user profile for social
  getUserProfile: (userId) => api.get(`${import.meta.env.VITE_API_URL}/api/v1/user-profile/${userId}?other_profile=true`),

  // Follow/unfollow user
  toggleFollow: (userId) => api.post(`${import.meta.env.VITE_API_URL}/api/v1/users/${userId}/follow`),

  // Get trending topics
  getTrendingTopics: (params = {}) => api.get(`${import.meta.env.VITE_API_URL}/api/v1/social/trending-topics`, { params }),

  // Get user activity feed
  getActivityFeed: (params = {}) => api.get(`${import.meta.env.VITE_API_URL}/api/v1/social/activity-feed`, { params }),
}

// Stories API endpoints
export const storiesAPI = {
  // Get stories feed
  getStoriesFeed: (params = {}) => api.get(`${import.meta.env.VITE_API_URL}/api/v1/stories/feed`, { params }),

  // Create new story
  // Create new story
  createStory: (storyData, onUploadProgress) => {
    const formData = new FormData()

    // Handle multiple files
    if (storyData.files && Array.isArray(storyData.files)) {
      storyData.files.forEach((file) => {
        formData.append('media[]', file)
      })
    } else if (storyData.file) {
      // Backward compatibility for single file
      formData.append('media', storyData.file)
    }

    formData.append('caption', storyData.caption || '')
    formData.append('status', 'approved')

    // Set expiration to 24 hours from now
    const expiresAt = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString()
    formData.append('expires_at', expiresAt)

    return api.post(`${import.meta.env.VITE_API_URL}/api/v1/stories`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onUploadProgress) {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onUploadProgress(percentCompleted);
        }
      }
    })
  },

  // Get user's stories
  getUserStories: (userId, params = {}) => api.get(`${import.meta.env.VITE_API_URL}/api/v1/stories/user/${userId}`, { params }),

  // Get single story
  getStory: (storyId) => api.get(`${import.meta.env.VITE_API_URL}/api/v1/stories/${storyId}`),

  // Delete story
  deleteStory: (storyId) => api.delete(`${import.meta.env.VITE_API_URL}/api/v1/stories/${storyId}`),

  // View story (mark as viewed)
  viewStory: (storyId) => api.post(`${import.meta.env.VITE_API_URL}/api/v1/stories/${storyId}/view`),

  // Get list of users who viewed a story (author-only)
  getStoryViewers: (storyId) => api.get(`${import.meta.env.VITE_API_URL}/api/v1/stories/${storyId}/views`),

  // React to a story
  reactToStory: (storyId, reactionType = 'like') => {
    const payload = {
      reactionable_type: "App\\Models\\Story",
      reactionable_id: storyId,
      type: reactionType
    };
    return api.post(`${import.meta.env.VITE_API_URL}/api/v1/reactions`, payload);
  },

  // Get general story reactions
  getStoryReactions: (storyId, params = {}) => {
    return api.get(`${import.meta.env.VITE_API_URL}/api/v1/reactions`, {
      params: {
        ...params,
        reactionable_type: "App\\Models\\Story",
        reactionable_id: storyId
      }
    });
  },
}

// Conversations/Chat API endpoints
export const conversationsAPI = {
  // Get all conversations/chats
  getChats: (params = {}) => api.get(`${import.meta.env.VITE_API_URL}/api/v1/conversations/chats`, { params }),
  getSocialChats: (params = {}) => api.get(`${import.meta.env.VITE_API_URL}/api/v1/conversations/chats/social`, { params }),
  getMedicalChats: (params = {}) => api.get(`${import.meta.env.VITE_API_URL}/api/v1/conversations/chats/medical`, { params }),

  // Get conversation by user ID (to resolve ambiguity vs conversation ID)
  getConversationByUser: (userId, type = 'social') => {
    // If type is not provided or null, default to 'social' or just pass nothing if backend handles it
    const url = type
      ? `${import.meta.env.VITE_API_URL}/api/v1/conversations/user/${userId}/${type}`
      : `${import.meta.env.VITE_API_URL}/api/v1/conversations/user/${userId}`;
    return api.get(url);
  },

  // Get single conversation
  getConversation: (conversationId) => api.get(`${import.meta.env.VITE_API_URL}/api/v1/conversations/${conversationId}`),

  // Get conversation by receiver ID
  getConversationByReceiver: (receiverId, conversationType, caseId = null, caseType = null) => {
    let url = `${import.meta.env.VITE_API_URL}/api/v1/conversations/receiver/${receiverId}/${conversationType}`;
    const params = new URLSearchParams();
    if (caseId) params.append('case_id', caseId);
    if (caseType) params.append('case_type', caseType);
    const queryString = params.toString();
    if (queryString) url += `?${queryString}`;
    return api.get(url);
  },

  // Get messages for a conversation
  getMessages: (conversationId, params = {}) => api.get(`${import.meta.env.VITE_API_URL}/api/v1/conversations/${conversationId}/messages`, { params }),

  // Send message to conversation
  sendMessage: (conversationId, messageData) => {
    // Get socket_id if available and include it in the payload
    const payload = { ...messageData }
    const deviceKey = localStorage.getItem("device_id").toString();
    payload.device_key = deviceKey;
    return api.post(`${import.meta.env.VITE_API_URL}/api/v1/conversations/${conversationId}/messages`, payload)
  },

  // Send message with media files
  sendMessageWithMedia: (conversationId, content, mediaFiles, duration = null) => {
    const formData = new FormData()
    formData.append('content', content)

    if (mediaFiles && mediaFiles.length > 0) {
      mediaFiles.forEach((file, index) => {
        formData.append('media[]', file)
      })
    }

    if (duration) {
      formData.append('duration', duration)
    }

    const deviceKey = localStorage.getItem("device_id").toString();
    formData.append('device_key', deviceKey);

    return api.post(`${import.meta.env.VITE_API_URL}/api/v1/conversations/${conversationId}/messages`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  },

  // Create new conversation
  createConversation: (participantIds, type = 'social', caseId = null, caseType = null) => {
    // Handle both single ID and array of IDs
    const ids = Array.isArray(participantIds) ? participantIds : [participantIds];
    return api.post(`${import.meta.env.VITE_API_URL}/api/v1/conversations`, {
      participant_id: ids[0],
      type,
      case_id: caseId,
      case_type: caseType
    });
  },

  // Mark conversation as read
  markAsRead: (conversationId, data = { mark_all: true }) => api.patch(`${import.meta.env.VITE_API_URL}/api/v1/conversations/${conversationId}/messages/read`, data),

  // Close conversation
  closeConversation: (conversationId) => api.patch(`${import.meta.env.VITE_API_URL}/api/v1/conversations/${conversationId}/close`),

  // Hide conversation
  hideConversation: (conversationId) => api.post(`${import.meta.env.VITE_API_URL}/api/v1/conversations/${conversationId}/hide`),

  // Delete conversation
  deleteConversation: (conversationId) => api.delete(`${import.meta.env.VITE_API_URL}/api/v1/conversations/${conversationId}`),

  // Delete message
  deleteMessage: (conversationId, messageId) => {
    const deviceKey = localStorage.getItem("device_id").toString();
    return api.delete(`${import.meta.env.VITE_API_URL}/api/v1/conversations/${conversationId}/messages/${messageId}`, {
      data: { device_key: deviceKey }
    })
  },
}

// FIXED: Enhanced error handling utility
export const handleApiError = (error) => {
  if (error.response) {
    // Server responded with error status
    const { status, data } = error.response

    switch (status) {
      case 400:
        return data.message || 'Bad request'
      case 401:
        return 'Session expired. Please log in again.'
      case 403:
        return 'You do not have permission to perform this action.'
      case 404:
        return 'The requested resource was not found.'
      case 422:
        // Validation errors
        if (data.errors) {
          const firstError = Object.values(data.errors)[0]
          return Array.isArray(firstError) ? firstError[0] : firstError
        }
        return data.message || 'Invalid data provided.'
      case 429:
        return 'Too many requests. Please try again later.'
      case 500:
        return 'Server error occurred. Please try again later.'
      case 503:
        return 'Service temporarily unavailable. Please try again later.'
      default:
        return data.message || `Request failed with status ${status}`
    }
  } else if (error.request) {
    // Network error
    return 'Network error. Please check your internet connection.'
  } else if (error.message) {
    // Handle specific error messages
    if (error.message.includes('Authentication required')) {
      return 'Authentication required. Please log in.'
    }
    return error.message
  } else {
    // Other error
    return 'An unexpected error occurred. Please try again.'
  }
}

// Urgent Case API
export const urgentCaseAPI = {
  // Submit urgent medical case
  submitUrgentCase: (formData) => {
    return api.post(`${import.meta.env.VITE_API_URL}/api/v1/pet-health/urgent-cases`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  },

  // Get acceptable (pending, unassigned) urgent cases for queue
  getAcceptableCases: (params = {}) => {
    return api.get(`${import.meta.env.VITE_API_URL}/api/v1/pet-health/urgent-cases/acceptable`, { params })
  },

  // Accept case wrapper
  acceptCase: (caseId) => api.post(`${import.meta.env.VITE_API_URL}/api/v1/pet-health/urgent-cases/${caseId}/accept`),

  // Reject case wrapper
  rejectCase: (caseId) => api.post(`${import.meta.env.VITE_API_URL}/api/v1/pet-health/urgent-cases/${caseId}/reject`),

  // Get urgent case by ID
  getUrgentCase: (urgentCaseId) => {
    return api.get(`${import.meta.env.VITE_API_URL}/api/v1/pet-health/urgent-cases/${urgentCaseId}`)
  },
  // Get active urgent cases for user
  getActiveUrgentCasesForVeterinarian: () => {
    return api.get(`${import.meta.env.VITE_API_URL}/api/v1/pet-health/urgent-cases/active/veterinarian`)
  },
  // Get active urgent cases for user
  getActiveUrgentCases: () => {
    return api.get(`${import.meta.env.VITE_API_URL}/api/v1/pet-health/urgent-cases/active`)
  },

  // Extend case expiration
  extendCaseExpiration: (caseId) => {
    return api.post(`${import.meta.env.VITE_API_URL}/api/v1/pet-health/urgent-cases/${caseId}/extend-expiration`)
  },

  // End case
  endCase: (caseId) => {
    return api.post(`${import.meta.env.VITE_API_URL}/api/v1/pet-health/urgent-cases/${caseId}/close`)
  },

  // Rate urgent case
  rateUrgentCase: (caseId, ratingData) => {
    return api.post(`${import.meta.env.VITE_API_URL}/api/v1/pet-health/urgent-cases/${caseId}/rate`, ratingData)
  },
}

// Clinic API
export const clinicAPI = {
  // Respond to clinic invitation
  respondToInvitation: (invitationId, action) => {
    return api.post(`${import.meta.env.VITE_API_URL}/api/v1/clinic-members/${invitationId}/${action}`);
  },

  // Invite an existing veterinarian to the clinic
  inviteExistingDoctor: (data) => api.post(`${import.meta.env.VITE_API_URL}/api/v1/clinic-members`, data),

  // Cancel a pending invitation or remove an accepted member
  removeClinicMember: (memberId) => api.delete(`${import.meta.env.VITE_API_URL}/api/v1/clinic-members/${memberId}`),

  // Invite a non-registered veterinarian by email
  inviteVetByEmail: (data) => api.post(`${import.meta.env.VITE_API_URL}/api/v1/clinic-members/invite-by-email`, data),

  // Invite a non-registered clinic moderator by email
  inviteModeratorByEmail: (data) => api.post(`${import.meta.env.VITE_API_URL}/api/v1/clinic-members/invite-moderator-by-email`, data),

  // Get all clinics with optional search
  // Backwards compatible:
  // - getClinics("foo") => { search: "foo" }
  // - getClinics({ search, per_page, page, ... }) => pass-through params
  getClinics: (searchTermOrParams = '') => {
    const params = typeof searchTermOrParams === 'object' && searchTermOrParams !== null
      ? searchTermOrParams
      : (searchTermOrParams ? { search: String(searchTermOrParams).trim() } : {})
    return api.get(`${import.meta.env.VITE_API_URL}/api/v1/clinics`, { params })
  },

  // Get clinic by ID
  getClinicById: (clinicId) => api.get(`${import.meta.env.VITE_API_URL}/api/v1/clinics/${clinicId}`),
  getVetClinic: () => api.get(`${import.meta.env.VITE_API_URL}/api/v1/clinics`),

  // Get clinic services
  getClinicServices: (clinicId) => api.get(`${import.meta.env.VITE_API_URL}/api/v1/clinics/${clinicId}/services`),

  // Get clinic team members
  getClinicTeam: (clinicId) => api.get(`${import.meta.env.VITE_API_URL}/api/v1/clinics/${clinicId}/team`),

  // Get veterinarians for a clinic (filter API)
  getVeterinarians: (params = {}) => api.get(`${import.meta.env.VITE_API_URL}/api/v1/veterinarians/filter`, { params }),

  // Get clinic appointments
  getClinicAppointments: (clinicId) => api.get(`${import.meta.env.VITE_API_URL}/api/v1/clinics/${clinicId}/appointments`),

  // Book appointment
  bookAppointment: (appointmentData) => api.post(`${import.meta.env.VITE_API_URL}/api/v1/appointments`, appointmentData),

  // Get my appointments
  getMyAppointments: () => api.get(`${import.meta.env.VITE_API_URL}/api/v1/appointments/my-appointments`),

  // Cancel appointment
  cancelAppointment: (appointmentId, canceled_reason = "Canceled by user") => api.post(`${import.meta.env.VITE_API_URL}/api/v1/clinic-appointments/${appointmentId}/cancel`, { canceled_reason }),

  // Reschedule appointment
  rescheduleAppointment: (appointmentId, rescheduleData) => {
    // Handle both old format (just datetime) and new format (full data object)
    const requestData = typeof rescheduleData === 'string' ?
      { appointment_date: rescheduleData } :
      rescheduleData;

    return api.put(`${import.meta.env.VITE_API_URL}/api/v1/clinic-appointments/${appointmentId}`, requestData);
  },

  // Search guardians
  searchGuardians: (searchTerm = '') => {
    const params = searchTerm ? { name: searchTerm.trim() } : {}
    return api.get(`${import.meta.env.VITE_API_URL}/api/v1/guardians/search`, { params })
  },

  // Get clinic working hours
  getWorkingHours: (clinicId) => api.get(`${import.meta.env.VITE_API_URL}/api/v1/clinic-working-hours/${clinicId}`),

  // Search clinics
  searchClinics: (query = '') => {
    const params = query ? { query: query.trim() } : {}
    return api.get(`${import.meta.env.VITE_API_URL}/api/v1/clinics/search`, { params })
  },

  // Get vet availability for a specific date
  getVetAvailability: (vetId, date) => {
    return api.get(`${import.meta.env.VITE_API_URL}/api/v1/clinic-appointments/vet-availability`, {
      params: { vet_id: vetId, date }
    })
  },

  // Update clinic (can be used to update verification_status)
  updateClinic: (clinicId, clinicData) => {
    return api.put(`${import.meta.env.VITE_API_URL}/api/v1/clinics/${clinicId}`, clinicData);
  },

  // Get clinic setup data (consolidated data for clinic setup)
  getClinicSetupData: (params = {}) => {
    // params can include: clinic_id, user_id, country_id
    return api.get(CLINIC_SETUP_DATA_ENDPOINT, { params });
  }
}

// Admin API endpoints
export const adminAPI = {
  // Verify or reject a clinic
  verifyClinic: withAuditLog(
    ACTION_TYPES.CLINIC_VERIFY.key,
    (clinicId, status, rejectedReason = null) => {
      const requestBody = {
        status: status // 'verified' or 'rejected'
      };
      if (status === 'rejected') {
        requestBody.rejected_reason = rejectedReason || null;
      }
      return api.post(`${import.meta.env.VITE_API_URL}/api/v1/admin/clinics/${clinicId}/verify`, requestBody);
    },
    (clinicId, status, rejectedReason) => ({
      targetId: clinicId,
      targetType: 'clinic',
      actionType: status === 'rejected' ? ACTION_TYPES.CLINIC_REJECT.key : ACTION_TYPES.CLINIC_VERIFY.key,
      details: { status, rejected_reason: rejectedReason },
    })
  ),

  // Upgrade clinic subscription from free to paid
  upgradeClinicSubscription: withAuditLog(
    ACTION_TYPES.CLINIC_UPGRADE_SUB.key,
    (clinicId) => {
      return api.post(`${import.meta.env.VITE_API_URL}/api/v1/admin/clinics/${clinicId}/upgrade-subscription`, {
        plan: 'paid'
      });
    },
    (clinicId) => ({ targetId: clinicId, targetType: 'clinic' })
  ),

  // Downgrade clinic subscription from paid to free
  downgradeClinicSubscription: withAuditLog(
    ACTION_TYPES.CLINIC_DOWNGRADE_SUB.key,
    (clinicId) => {
      return api.post(`${import.meta.env.VITE_API_URL}/api/v1/admin/clinics/${clinicId}/upgrade-subscription`, {
        plan: 'free'
      });
    },
    (clinicId) => ({ targetId: clinicId, targetType: 'clinic' })
  ),

  // Submit upgrade interest - marks clinic as having requested upgrade
  submitUpgradeInterest: (clinicId) => {
    return api.post(`${import.meta.env.VITE_API_URL}/api/v1/admin/clinics/${clinicId}/submit-upgrade-interest`);
  },

  // Send system-wide announcement
  sendAnnouncement: withAuditLog(
    ACTION_TYPES.SYSTEM_ANNOUNCEMENT.key,
    (announcementData) => {
      return api.post(`${import.meta.env.VITE_API_URL}/api/v1/admin/send-announcement`, announcementData);
    },
    (announcementData) => ({
      targetType: 'system',
      details: { title: announcementData?.title, type: announcementData?.type },
    })
  ),

  // Broadcast chat message to all users
  broadcastChatMessage: withAuditLog(
    ACTION_TYPES.SYSTEM_BULK_MESSAGE.key,
    (messageData) => {
      return api.post(`${import.meta.env.VITE_API_URL}/api/v1/admin/broadcast-message`, messageData);
    },
    (messageData) => ({
      targetType: 'system',
      details: { message_preview: messageData?.message?.substring(0, 100) },
    })
  ),

  // App Version management
  getAppVersion: () => api.get(`${import.meta.env.VITE_API_URL}/api/v1/app-version`),
  updateAppVersion: withAuditLog(
    ACTION_TYPES.APP_VERSION_UPDATE.key,
    (versionData) => api.post(`${import.meta.env.VITE_API_URL}/api/v1/admin/app-version`, versionData),
    (versionData) => ({
      targetType: 'system',
      details: { title_en: versionData?.title_en, title_ar: versionData?.title_ar },
    })
  ),
}
// Create clinic appointment
export const createClinicAppointment = (data) => {
  const token = localStorage.getItem("access_token");
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  return api.post(`${import.meta.env.VITE_API_URL}/api/v1/clinic-appointments`, data, { headers });
};

// Get user's clinic appointments
// Include all statuses to ensure upcoming appointments are returned
export const getUserClinicAppointments = (params = {}) => {
  // Don't filter by status - get all appointments for the user
  // Backend should return all appointments regardless of status
  return api.get(`${import.meta.env.VITE_API_URL}/api/v1/clinic-appointments/user`, { params });
};

// Outpatient Visits API
export const outpatientAPI = {
  // Create new outpatient visit
  createOutpatientVisit: (visitData) => {
    return api.post(`${import.meta.env.VITE_API_URL}/api/v1/outpatient-visits`, visitData);
  },

  // Get outpatient visits
  getOutpatientVisits: (params = {}) => {
    return api.get(`${import.meta.env.VITE_API_URL}/api/v1/outpatient-visits`, { params });
  },

  // Get single outpatient visit
  getOutpatientVisit: (visitId) => {
    return api.get(`${import.meta.env.VITE_API_URL}/api/v1/outpatient-visits/${visitId}`);
  },

  // Update outpatient visit
  updateOutpatientVisit: (visitId, visitData) => {
    return api.put(`${import.meta.env.VITE_API_URL}/api/v1/outpatient-visits/${visitId}`, visitData);
  },

  // Delete outpatient visit
  deleteOutpatientVisit: (visitId) => {
    return api.delete(`${import.meta.env.VITE_API_URL}/api/v1/outpatient-visits/${visitId}`);
  },

  // Convert an appointment into an outpatient visit
  convertFromAppointment: (appointmentId, downpayment = 0, extra = {}) => {
    return api.post(`${import.meta.env.VITE_API_URL}/api/v1/outpatient-visits/convert-from-appointment`, {
      appointment_id: appointmentId,
      downpayment: downpayment,
      ...extra,
    });
  },

  // Update outpatient visit details
  updateOutpatientVisitDetails: (outpatientVisitDetailId, visitDetailsData) => {
    return api.put(`${import.meta.env.VITE_API_URL}/api/v1/outpatient-visit-details/${outpatientVisitDetailId}`, visitDetailsData);
  },

  // Create outpatient visit details
  createOutpatientVisitDetails: (visitDetailsData) => {
    return api.post(`${import.meta.env.VITE_API_URL}/api/v1/outpatient-visit-details`, visitDetailsData);
  },

  // Add service to outpatient visit
  addServiceToOutpatientVisit: (serviceData) => {
    return api.post(`${import.meta.env.VITE_API_URL}/api/v1/outpatient-visit-details/services/add`, serviceData);
  },

  // Close outpatient visit
  closeOutpatientVisit: (outpatientId, data = {}) => {
    return api.post(`${import.meta.env.VITE_API_URL}/api/v1/outpatient-visits/${outpatientId}/close`, data);
  },

  // Update service in outpatient visit
  updateServiceInOutpatientVisit: (visitDetailId, serviceData) => {
    return api.put(`${import.meta.env.VITE_API_URL}/api/v1/outpatient-visit-details/services/${visitDetailId}`, serviceData);
  },

  // Delete service from outpatient visit
  deleteServiceFromOutpatientVisit: (visitDetailId) => {
    return api.delete(`${import.meta.env.VITE_API_URL}/api/v1/outpatient-visit-details/services/${visitDetailId}`);
  },

  // Get outpatient visit details
  getOutpatientVisitDetails: (visitId) => {
    return api.get(`${import.meta.env.VITE_API_URL}/api/v1/outpatient-visits/${visitId}/details`);
  },

  // Bulk update outpatient visit (services and initial diagnosis)
  bulkUpdateOutpatientVisit: (outpatientId, bulkData) => {
    return api.put(`${import.meta.env.VITE_API_URL}/api/v1/outpatient-visits/${outpatientId}/bulk-update`, bulkData);
  },

  // Attachments
  uploadAttachment: (outpatientId, file, onUploadProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`${import.meta.env.VITE_API_URL}/api/v1/outpatient-visits/${outpatientId}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress
    });
  },

  deleteAttachment: (outpatientId, attachmentId) => {
    return api.delete(`${import.meta.env.VITE_API_URL}/api/v1/outpatient-visits/${outpatientId}/attachments/${attachmentId}`);
  },

  getAttachments: (outpatientId) => {
    return api.get(`${import.meta.env.VITE_API_URL}/api/v1/outpatient-visits/${outpatientId}/attachments`);
  },
};

export const inpatientAPI = {
  // Create new inpatient visit
  createInpatientVisit: (inpatientData) => {
    return api.post(`${import.meta.env.VITE_API_URL}/api/v1/inpatient-visits`, inpatientData);
  },

  // Convert an appointment into an inpatient visit
  convertFromAppointment: (appointmentId, downpayment = 0, extra = {}) => {
    return api.post(`${import.meta.env.VITE_API_URL}/api/v1/inpatient-visits/convert-from-appointment`, {
      appointment_id: appointmentId,
      downpayment: downpayment,
      ...extra,
    });
  },

  // Get inpatient visit by ID
  getInpatientVisit: (inpatientId) => {
    return api.get(`${import.meta.env.VITE_API_URL}/api/v1/inpatient-visits/${inpatientId}`);
  },

  // Update inpatient visit
  updateInpatientVisit: (inpatientId, inpatientData) => {
    return api.put(`${import.meta.env.VITE_API_URL}/api/v1/inpatient-visits/${inpatientId}`, inpatientData);
  },

  // Get inpatient visit details
  getInpatientVisitDetails: (visitId) => {
    return api.get(`${import.meta.env.VITE_API_URL}/api/v1/inpatient-visits/${visitId}/details`);
  },

  // Bulk update inpatient visit (services and initial diagnosis)
  bulkUpdateInpatientVisit: (inpatientId, bulkData) => {
    return api.put(`${import.meta.env.VITE_API_URL}/api/v1/inpatient-visits/${inpatientId}/bulk-update`, bulkData);
  },

  // Add service to inpatient visit
  addServiceToInpatientVisit: (serviceData) => {
    return api.post(`${import.meta.env.VITE_API_URL}/api/v1/inpatient-visit-details/services/add`, serviceData);
  },

  // Update service in inpatient visit
  updateServiceInInpatientVisit: (visitDetailId, serviceData) => {
    return api.put(`${import.meta.env.VITE_API_URL}/api/v1/inpatient-visit-details/services/${visitDetailId}`, serviceData);
  },

  // Delete service from inpatient visit
  deleteServiceFromInpatientVisit: (visitDetailId) => {
    return api.delete(`${import.meta.env.VITE_API_URL}/api/v1/inpatient-visit-details/services/${visitDetailId}`);
  },

  // Close inpatient visit (discharge)
  closeInpatientVisit: (inpatientId, data = {}) => {
    return api.post(`${import.meta.env.VITE_API_URL}/api/v1/inpatient-visits/${inpatientId}/close`, data);
  },

  // Delete inpatient visit
  deleteInpatientVisit: (inpatientId) => {
    return api.delete(`${import.meta.env.VITE_API_URL}/api/v1/inpatient-visits/${inpatientId}`);
  },

  // Convert an outpatient visit into an inpatient visit
  convertFromOutpatient: (outpatientId, downpayment = 0, extra = {}) => {
    return api.post(`${import.meta.env.VITE_API_URL}/api/v1/inpatient-visits/convert-from-outpatient`, {
      outpatient_visit_id: outpatientId,
      downpayment: downpayment,
      ...extra,
    });
  },

  // Get vet orders for an inpatient visit
  getVetOrders: (inpatientId) => {
    return api.get(`${import.meta.env.VITE_API_URL}/api/v1/vet-orders/inpatient-visit/${inpatientId}`);
  },

  // Bulk sync vet orders (create/update/delete)
  bulkSyncVetOrders: (data) => {
    return api.post(`${import.meta.env.VITE_API_URL}/api/v1/vet-orders/bulk-sync`, data);
  },

  // Attachments
  uploadAttachment: (inpatientId, file, onUploadProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`${import.meta.env.VITE_API_URL}/api/v1/inpatient-visits/${inpatientId}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress
    });
  },

  deleteAttachment: (inpatientId, attachmentId) => {
    return api.delete(`${import.meta.env.VITE_API_URL}/api/v1/inpatient-visits/${inpatientId}/attachments/${attachmentId}`);
  },

  getAttachments: (inpatientId) => {
    return api.get(`${import.meta.env.VITE_API_URL}/api/v1/inpatient-visits/${inpatientId}/attachments`);
  },

  getSoapNoteByInpatientId: (inpatientId) => {
    return api.get(`${import.meta.env.VITE_API_URL}/api/v1/soap-notes/inpatient-visit/${inpatientId}`);
  },
}
// Clinic Appointments API
export const clinicAppointmentsAPI = {
  // Check if user has clinic
  haveClinic: () => {
    return api.get(`${import.meta.env.VITE_API_URL}/api/v1/users/have-clinic`);
  },
  // Search appointments with comprehensive filters
  searchAppointments: (params = {}) => {
    return api.get(`${import.meta.env.VITE_API_URL}/api/v1/clinic-appointments/search`, { params });
  },
  // Search appointments with comprehensive filters
  searchOutpatientVisits: (params = {}) => {
    return api.get(`${import.meta.env.VITE_API_URL}/api/v1/outpatient-visits/search`, { params });
  },

  // Search inpatient visits with comprehensive filters
  searchInpatientVisits: (params = {}) => {
    return api.get(`${import.meta.env.VITE_API_URL}/api/v1/inpatient-visits/search`, { params });
  },

  // Get current day appointments and visits (all in one call)
  getCurrentDayData: (clinicId) => {
    return api.get(`${import.meta.env.VITE_API_URL}/api/v1/clinic-appointments/current-day`, {
      params: { clinic_id: clinicId }
    });
  },

  // Get appointments for a clinic
  getClinicAppointments: (clinicId, params = {}) => {
    return api.get(`${import.meta.env.VITE_API_URL}/api/v1/clinics/${clinicId}/appointments`, { params });
  },

  // Create new appointment
  createAppointment: (appointmentData) => {
    return api.post(`${import.meta.env.VITE_API_URL}/api/v1/clinic-appointments`, appointmentData);
  },

  // Update appointment
  updateAppointment: (appointmentId, appointmentData) => {
    return api.put(`${import.meta.env.VITE_API_URL}/api/v1/clinic-appointments/${appointmentId}`, appointmentData);
  },

  // Cancel appointment (soft cancel)
  cancelAppointment: (appointmentId, canceled_reason = "Canceled by clinic") => {
    return api.post(`${import.meta.env.VITE_API_URL}/api/v1/clinic-appointments/${appointmentId}/cancel`, { canceled_reason });
  },

  // Delete appointment
  deleteAppointment: (appointmentId) => {
    return api.delete(`${import.meta.env.VITE_API_URL}/api/v1/clinic-appointments/${appointmentId}`);
  },

  // Get single appointment
  getAppointment: (appointmentId) => {
    return api.get(`${import.meta.env.VITE_API_URL}/api/v1/clinic-appointments/${appointmentId}`);
  }
};

export const channelsAPI = {
  getChannels: (params = {}) => {
    return api.get(`${import.meta.env.VITE_API_URL}/api/v1/channels`, { params });
  },

  getChannel: (channelId) => {
    return api.get(`${import.meta.env.VITE_API_URL}/api/v1/channels/${channelId}`);
  },

  createChannel: (channelData) => {
    const formData = new FormData();

    Object.keys(channelData).forEach(key => {
      if (key !== 'coverImage' && key !== 'profileImage') {
        if (channelData[key] !== null && channelData[key] !== undefined) {
          formData.append(key, channelData[key]);
        }
      }
    });

    if (channelData.coverImage) {
      formData.append('cover_image', channelData.coverImage);
    }

    if (channelData.profileImage) {
      formData.append('profile_image', channelData.profileImage);
    }

    return api.post(`${import.meta.env.VITE_API_URL}/api/v1/channels`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  updateChannel: (channelId, channelData) => {
    // If we are only sending JSON-friendly fields (no files),
    // send a real PUT JSON request so arrays/objects arrive correctly.
    const hasFiles = !!(channelData.coverImage || channelData.profileImage);
    if (!hasFiles) {
      return api.put(
        `${import.meta.env.VITE_API_URL}/api/v1/channels/${channelId}`,
        channelData
      );
    }

    const formData = new FormData();
    formData.append("_method", "PUT");

    Object.keys(channelData).forEach((key) => {
      if (key !== "coverImage" && key !== "profileImage") {
        const value = channelData[key];
        if (value !== null && value !== undefined) {
          // For arrays/objects, send JSON so backend can decode properly.
          if (typeof value === "object") {
            formData.append(key, JSON.stringify(value));
          } else {
            formData.append(key, value);
          }
        }
      }
    });

    if (channelData.coverImage) {
      formData.append("cover_image", channelData.coverImage);
    }

    if (channelData.profileImage) {
      formData.append("profile_image", channelData.profileImage);
    }

    return api.post(
      `${import.meta.env.VITE_API_URL}/api/v1/channels/${channelId}`,
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
      }
    );
  },

  deleteChannel: (channelId) => {
    return api.delete(`${import.meta.env.VITE_API_URL}/api/v1/channels/${channelId}`);
  },

  joinChannel: (channelId) => {
    return api.post(`${import.meta.env.VITE_API_URL}/api/v1/channels/${channelId}/join`);
  },

  leaveChannel: (channelId) => {
    return api.post(`${import.meta.env.VITE_API_URL}/api/v1/channels/${channelId}/leave`);
  },

  requestJoinChannel: (channelId, message = '') => {
    return api.post(`${import.meta.env.VITE_API_URL}/api/v1/channels/${channelId}/join-request`, {
      message
    });
  },

  cancelJoinRequest: (channelId) => {
    return api.delete(`${import.meta.env.VITE_API_URL}/api/v1/channels/${channelId}/join-request`);
  },

  inviteMembers: (channelId, userIds) => {
    return api.post(`${import.meta.env.VITE_API_URL}/api/v1/channels/${channelId}/invite`, {
      user_ids: userIds
    });
  },

  // New: channel invitations (for pending invites)
  getChannelInvitations: (params = {}) => {
    return api.get(`${import.meta.env.VITE_API_URL}/api/v1/channel-invitations`, { params });
  },

  acceptInvitation: (invitationId) => {
    return api.post(`${import.meta.env.VITE_API_URL}/api/v1/channel-invitations/${invitationId}/accept`);
  },

  declineInvitation: (invitationId) => {
    return api.post(`${import.meta.env.VITE_API_URL}/api/v1/channel-invitations/${invitationId}/decline`);
  },

  getMyChannels: (params = {}) => {
    return api.get(`${import.meta.env.VITE_API_URL}/api/v1/channels/my-channels`, { params });
  },

  discoverChannels: (params = {}) => {
    return api.get(`${import.meta.env.VITE_API_URL}/api/v1/channels/discover`, { params });
  },

  getChannelMembers: (channelId, params = {}) => {
    return api.get(`${import.meta.env.VITE_API_URL}/api/v1/channels/${channelId}/members`, { params });
  },

  getChannelJoinRequests: (channelId, params = {}) => {
    return api.get(`${import.meta.env.VITE_API_URL}/api/v1/channels/${channelId}/join-requests`, { params });
  },

  approveJoinRequest: (channelId, requestId) => {
    return api.post(`${import.meta.env.VITE_API_URL}/api/v1/channels/${channelId}/join-requests/${requestId}/approve`);
  },

  rejectJoinRequest: (channelId, requestId) => {
    return api.post(`${import.meta.env.VITE_API_URL}/api/v1/channels/${channelId}/join-requests/${requestId}/reject`);
  },

  transferOwnership: (channelId, newOwnerId) => {
    return api.post(`${import.meta.env.VITE_API_URL}/api/v1/channels/${channelId}/transfer-ownership`, {
      new_owner_id: newOwnerId
    });
  },

  assignAdmin: (channelId, memberId) => {
    return api.put(
      `${import.meta.env.VITE_API_URL}/api/v1/channels/${channelId}/members/${memberId}/role`,
      { role: "admin" }
    );
  },

  removeAdmin: (channelId, memberId) => {
    return api.put(
      `${import.meta.env.VITE_API_URL}/api/v1/channels/${channelId}/members/${memberId}/role`,
      { role: "member" }
    );
  },

  removeMember: (channelId, memberId) => {
    return api.delete(
      `${import.meta.env.VITE_API_URL}/api/v1/channels/${channelId}/members/${memberId}`
    );
  },

  pinPost: (channelId, postId) => {
    return api.post(`${import.meta.env.VITE_API_URL}/api/v1/channels/${channelId}/pin-post`, {
      post_id: postId,
    });
  },

  unpinPost: (channelId, postId) => {
    return api.post(`${import.meta.env.VITE_API_URL}/api/v1/channels/${channelId}/unpin-post`, {
      post_id: postId,
    });
  },

  getChannelEvents: (channelId, params = {}) => {
    return api.get(`${import.meta.env.VITE_API_URL}/api/v1/channels/${channelId}/events`, { params });
  },

  createChannelEvent: (channelId, eventData) => {
    return api.post(`${import.meta.env.VITE_API_URL}/api/v1/channels/${channelId}/events`, eventData);
  },

  syncChannelEvents: (channelId, events) => {
    return api.post(`${import.meta.env.VITE_API_URL}/api/v1/channels/${channelId}/events/sync`, { events });
  },

  updateChannelEvent: (channelId, eventId, eventData) => {
    return api.put(`${import.meta.env.VITE_API_URL}/api/v1/channels/${channelId}/events/${eventId}`, eventData);
  },

  deleteChannelEvent: (channelId, eventId) => {
    return api.delete(`${import.meta.env.VITE_API_URL}/api/v1/channels/${channelId}/events/${eventId}`);
  },
};


// ============================================================================
// USER PRESENCE & TYPING INDICATORS
// ============================================================================

/**
 * Update typing status for a conversation
 */
export const updateTypingStatus = async (conversationId, isTyping) => {
  try {
    const response = await api.post(`/api/v1/presence/typing/${conversationId}`, {
      is_typing: isTyping
    });
    return response.data;
  } catch (error) {
    console.error('Error updating typing status:', error);
    throw error;
  }
};

/**
 * Send heartbeat to update online status
 */
export const updateOnlineStatus = async () => {
  try {
    const response = await api.post('/api/v1/presence/online');
    return response.data;
  } catch (error) {
    console.error('Error updating online status:', error);
    throw error;
  }
};

/**
 * Get user's online/last seen status
 */
export const getUserPresenceStatus = async (userId) => {
  try {
    const response = await api.get(`/api/v1/presence/status/${userId}`);
    return response.data;
  } catch (error) {
    console.error('Error getting user presence status:', error);
    throw error;
  }
};

// Reports API endpoints
export const reportsAPI = {
  // Get available report reasons
  getReasons: (type = null) => {
    const params = { lang: i18n.language };
    if (type) params.type = type;
    return api.get('/api/v1/reports/reasons', { params });
  },

  // Check if user already reported an item (legacy - uses generic route)
  checkReport: (reportableType, reportableId) =>
    api.get('/api/v1/reports/check', {
      params: {
        reportable_type: reportableType,
        reportable_id: reportableId,
      },
    }),

  // Check if post is reported
  checkPost: (postId) =>
    api.get(`/api/v1/reports/posts/${postId}/check`),

  // Check if story is reported
  checkStory: (storyId) =>
    api.get(`/api/v1/reports/stories/${storyId}/check`),

  // Check if user is reported
  checkUser: (userId) =>
    api.get(`/api/v1/reports/users/${userId}/check`),

  // Create a new report (legacy - uses generic route)
  createReport: (data) =>
    api.post('/api/v1/reports', data),

  // Report a post
  reportPost: (postId, reason, customReason = null, blockUser = false, hidePost = false, muteUser = false) =>
    api.post(`/api/v1/reports/posts/${postId}`, {
      reason,
      custom_reason: customReason,
      block_user: blockUser,
      hide_post: hidePost,
      mute_user: muteUser,
    }),

  // Report a story
  reportStory: (storyId, reason, customReason = null, blockUser = false, muteUser = false) =>
    api.post(`/api/v1/reports/stories/${storyId}`, {
      reason,
      custom_reason: customReason,
      block_user: blockUser,
      mute_user: muteUser,
    }),

  // Report a user
  reportUser: (userId, reason, customReason = null, blockUser = false, muteUser = false) =>
    api.post(`/api/v1/reports/users/${userId}`, {
      reason,
      custom_reason: customReason,
      block_user: blockUser,
      mute_user: muteUser,
    }),

  // Get user's reports
  getReports: (params = {}) =>
    api.get('/api/v1/reports', { params }),

  // Get single report
  getReport: (reportId) =>
    api.get(`/api/v1/reports/${reportId}`),
};

// Notifications API endpoints
export const notificationsAPI = {
  // Get notifications with read status
  getNotificationsWithRead: (readType, params = {}) =>
    api.get(`${import.meta.env.VITE_API_URL}/api/v1/notifications/get-notifications-with-read/${readType}`, { params }),
};

// AI Chat API endpoints
export const aiChatAPI = {
  // Send a message to the AI chat and get a response
  // message: string - The user's message
  // yana_id: number (optional) - The Yana ID, defaults to 5 if not provided
  // top_k: number (optional) - The top_k parameter, defaults to 10
  sendMessage: (message, yana_id = 5, top_k = 10) => {
    return api.post(`${import.meta.env.VITE_APP_YANA_AI_URL}`, {
      question: message
    });
  },
};

export const supportAPI = {
  submitTicket: (ticketData) => {
    const formData = new FormData();
    formData.append('name', ticketData.name);
    formData.append('email', ticketData.email);
    formData.append('title', ticketData.title);
    formData.append('message', ticketData.message);
    if (ticketData.attachments) {
      formData.append('attachment', ticketData.attachments);
    }

    return api.post(`${import.meta.env.VITE_API_URL}/api/v1/support/submit`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }
};

// Dashboard API endpoints - re-export from dashboardService for consistency
export { dashboardAPI } from './dashboardService';

export const breedDataAPI = {
  getBreeds: (params = {}) => api.get(`${import.meta.env.VITE_API_URL}/api/v1/pet-health/breeds-data`, { params }),
  getBreed: (id) => api.get(`${import.meta.env.VITE_API_URL}/api/v1/pet-health/breeds-data/${id}`, { params: { by_primary_key: 1 } }),
  reorderBreeds: withAuditLog(
    ACTION_TYPES.CMS_BREED_REORDER.key,
    (ids) => api.post(`${import.meta.env.VITE_API_URL}/api/v1/pet-health/breeds-data/reorder`, { ids }),
    (ids) => ({ targetType: 'cms', details: { count: ids?.length } })
  ),
  createBreed: withAuditLog(
    ACTION_TYPES.CMS_BREED_CREATE.key,
    (breedData) => {
      const formData = new FormData();
      Object.keys(breedData).forEach(key => {
        if (breedData[key] !== null && breedData[key] !== undefined) {
          const value = typeof breedData[key] === 'boolean'
            ? (breedData[key] ? '1' : '0')
            : breedData[key];
          formData.append(key, value);
        }
      });
      return api.post(`${import.meta.env.VITE_API_URL}/api/v1/pet-health/breeds-data`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    },
    (breedData) => ({ targetType: 'cms', details: { name: breedData?.name } })
  ),
  updateBreed: withAuditLog(
    ACTION_TYPES.CMS_BREED_UPDATE.key,
    (id, breedData) => {
      const formData = new FormData();
      formData.append('_method', 'PUT');
      Object.keys(breedData).forEach(key => {
        if (breedData[key] !== null && breedData[key] !== undefined) {
          const value = typeof breedData[key] === 'boolean'
            ? (breedData[key] ? '1' : '0')
            : breedData[key];
          formData.append(key, value);
        }
      });
      return api.post(`${import.meta.env.VITE_API_URL}/api/v1/pet-health/breeds-data/${id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    },
    (id, breedData) => ({ targetId: id, targetType: 'cms', details: { name: breedData?.name } })
  ),
  deleteBreed: withAuditLog(
    ACTION_TYPES.CMS_BREED_DELETE.key,
    (id) => api.delete(`${import.meta.env.VITE_API_URL}/api/v1/pet-health/breeds-data/${id}`),
    (id) => ({ targetId: id, targetType: 'cms' })
  )
};

export const appBannerAPI = {
  getBanners: (params = {}) => api.get(`${import.meta.env.VITE_API_URL}/api/v1/app-banners`, { params }),
  getBanner: (id) => api.get(`${import.meta.env.VITE_API_URL}/api/v1/app-banners/${id}`),
  createBanner: withAuditLog(
    ACTION_TYPES.CMS_BANNER_CREATE.key,
    (bannerData) => {
      const formData = new FormData();
      Object.keys(bannerData).forEach(key => {
        if (bannerData[key] !== null && bannerData[key] !== undefined) {
          const value = typeof bannerData[key] === 'boolean'
            ? (bannerData[key] ? '1' : '0')
            : bannerData[key];
          formData.append(key, value);
        }
      });
      return api.post(`${import.meta.env.VITE_API_URL}/api/v1/app-banners`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    },
    (bannerData) => ({ targetType: 'cms', details: { title: bannerData?.title } })
  ),
  updateBanner: withAuditLog(
    ACTION_TYPES.CMS_BANNER_UPDATE.key,
    (id, bannerData) => {
      const formData = new FormData();
      formData.append('_method', 'PUT');
      Object.keys(bannerData).forEach(key => {
        if (bannerData[key] !== null && bannerData[key] !== undefined) {
          const value = typeof bannerData[key] === 'boolean'
            ? (bannerData[key] ? '1' : '0')
            : bannerData[key];
          formData.append(key, value);
        }
      });
      return api.post(`${import.meta.env.VITE_API_URL}/api/v1/app-banners/${id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    },
    (id, bannerData) => ({ targetId: id, targetType: 'cms', details: { title: bannerData?.title } })
  ),
  deleteBanner: withAuditLog(
    ACTION_TYPES.CMS_BANNER_DELETE.key,
    (id) => api.delete(`${import.meta.env.VITE_API_URL}/api/v1/app-banners/${id}`),
    (id) => ({ targetId: id, targetType: 'cms' })
  )
};

export const locationAPI = {
  getCountries: () => api.get(`${import.meta.env.VITE_API_URL}/api/v1/locations/countries`),
};

export const advertisementAPI = {
  getSummary: () => api.get('/api/v1/advertisements/summary'),
  getAdvertisements: (params = {}) => api.get('/api/v1/advertisements', { params }),
  getAdvertisement: (id) => api.get(`/api/v1/advertisements/${id}`),
  reorderAdvertisements: withAuditLog(
    ACTION_TYPES.CMS_AD_REORDER.key,
    (ids) => api.post('/api/v1/advertisements/reorder', { ids }),
    (ids) => ({ targetType: 'cms', details: { count: ids?.length } })
  ),
  createAdvertisement: withAuditLog(
    ACTION_TYPES.CMS_AD_CREATE.key,
    (adData) => {
      const formData = new FormData();
      Object.keys(adData).forEach(key => {
        if (key === 'media') {
          if (adData[key] instanceof File) {
            formData.append('media', adData[key]);
          }
        } else if (adData[key] !== null && adData[key] !== undefined) {
          const value = typeof adData[key] === 'boolean'
            ? (adData[key] ? '1' : '0')
            : adData[key];
          formData.append(key, value);
        }
      });
      return api.post('/api/v1/advertisements', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 600000,
      });
    },
    (adData) => ({ targetType: 'cms', details: { title: adData?.title } })
  ),
  updateAdvertisement: withAuditLog(
    ACTION_TYPES.CMS_AD_UPDATE.key,
    (id, adData) => {
      const formData = new FormData();
      formData.append('_method', 'PUT');
      Object.keys(adData).forEach(key => {
        if (key === 'media') {
          if (adData[key] instanceof File) {
            formData.append('media', adData[key]);
          }
        } else if (key === 'remove_media_ids' && Array.isArray(adData[key])) {
          adData[key].forEach(mediaId => {
            formData.append('remove_media_ids[]', mediaId);
          });
        } else if (adData[key] !== null && adData[key] !== undefined) {
          const value = typeof adData[key] === 'boolean'
            ? (adData[key] ? '1' : '0')
            : adData[key];
          formData.append(key, value);
        }
      });
      return api.post(`/api/v1/advertisements/${id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 600000,
      });
    },
    (id, adData) => ({ targetId: id, targetType: 'cms', details: { title: adData?.title } })
  ),
  deleteAdvertisement: withAuditLog(
    ACTION_TYPES.CMS_AD_DELETE.key,
    (id) => api.delete(`/api/v1/advertisements/${id}`),
    (id) => ({ targetId: id, targetType: 'cms' })
  )
};

export const speciesAPI = {
  getSpecies: (params = {}) => api.get(`${import.meta.env.VITE_API_URL}/api/v1/species`, { params }),
  getSpeciesById: (id) => api.get(`${import.meta.env.VITE_API_URL}/api/v1/species/${id}`),
  reorderSpecies: withAuditLog(
    ACTION_TYPES.CMS_SPECIES_REORDER.key,
    (ids) => api.post(`${import.meta.env.VITE_API_URL}/api/v1/species/reorder`, { ids }),
    (ids) => ({ targetType: 'cms', details: { count: ids?.length } })
  ),
  createSpecies: withAuditLog(
    ACTION_TYPES.CMS_SPECIES_CREATE.key,
    (speciesData) => {
      const formData = new FormData();
      Object.keys(speciesData).forEach(key => {
        if (speciesData[key] !== null && speciesData[key] !== undefined) {
          const value = typeof speciesData[key] === 'boolean'
            ? (speciesData[key] ? '1' : '0')
            : speciesData[key];
          formData.append(key, value);
        }
      });
      return api.post(`${import.meta.env.VITE_API_URL}/api/v1/species`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    },
    (speciesData) => ({ targetType: 'cms', details: { name: speciesData?.name } })
  ),
  updateSpecies: withAuditLog(
    ACTION_TYPES.CMS_SPECIES_UPDATE.key,
    (id, speciesData) => {
      const formData = new FormData();
      formData.append('_method', 'PUT');
      Object.keys(speciesData).forEach(key => {
        if (speciesData[key] !== null && speciesData[key] !== undefined) {
          const value = typeof speciesData[key] === 'boolean'
            ? (speciesData[key] ? '1' : '0')
            : speciesData[key];
          formData.append(key, value);
        }
      });
      return api.post(`${import.meta.env.VITE_API_URL}/api/v1/species/${id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    },
    (id, speciesData) => ({ targetId: id, targetType: 'cms', details: { name: speciesData?.name } })
  ),
  deleteSpecies: withAuditLog(
    ACTION_TYPES.CMS_SPECIES_DELETE.key,
    (id) => api.delete(`${import.meta.env.VITE_API_URL}/api/v1/species/${id}`),
    (id) => ({ targetId: id, targetType: 'cms' })
  ),
};

export const petReminderAPI = {
  getReminders: () => api.get(`${import.meta.env.VITE_API_URL}/api/v1/pet-reminders`),
  dismissReminder: (vaccinationId) => api.post(`${import.meta.env.VITE_API_URL}/api/v1/pet-reminders/${vaccinationId}/dismiss`),
};

export { api };
